import type { PersonaId } from "@/app/(marketing)/experts/_components/experts-hero-badge";
import type { OpportunityListingCard } from "@/components/opportunities/opportunity-card";

// Tuple-typed up front so AudienceSlug narrows to a literal union and the
// derived ExpertsAudienceSource (signup-source-types.ts) actually constrains
// values rather than collapsing to `experts_audience_${string}`.
export const AUDIENCE_SLUGS = [
  "finance",
  "accounting",
  "healthcare-ops",
  "software-engineering",
  "management-consultants",
  "product-experts",
  "data-scientists",
  "physicians",
  "nurses",
  "lawyers",
  "mechanical-engineers",
  "electrical-engineers",
  "civil-engineers",
  "cybersecurity",
  "economists",
  "marketing-experts",
  "sales-experts",
  "ux-designers",
  "pharmacists",
  "mental-health",
  "academics",
  "writers-editors",
  "translators",
  "actuaries",
  "quant-finance",
  "biology-life-sciences",
  "chemistry",
  "supply-chain",
  // Finance sub-specializations
  "fpa-analysts",
  "treasury-analysts",
  "credit-risk-analysts",
  "corporate-development",
  // Accounting sub-specializations
  "tax-accountants",
  "auditors",
  "controllers",
  "forensic-accountants",
  // Healthcare ops sub-specializations
  "medical-coders",
  "clinical-documentation",
  "revenue-cycle",
  "utilization-management",
  // Software engineering sub-specializations
  "backend-engineers",
  "frontend-engineers",
  "ml-engineers",
  "devops-sre",
  "data-engineers",
  // Management consulting sub-specializations
  "strategy-consultants",
  "operations-consultants",
  "ex-mbb-consultants",
  // Product sub-specializations
  "product-managers",
  "technical-program-managers",
  "growth-product-managers",
  "ai-product-managers",
  // Medical specialties (physician verticals; child pages of /experts/physicians)
  "primary-care",
  "internal-medicine",
  "family-medicine",
  "hospital-medicine",
  "pediatrics",
  "medical-oncology",
  "oncology-subspecialties",
  "radiation-oncology",
  "surgical-oncology",
  "neurology",
  "psychiatry",
  "neurosurgery",
  "cardiology",
  "interventional-cardiology",
  "pulmonology",
  "respiratory-medicine",
  "critical-care",
  "radiology",
  "pathology",
  "obstetrics-gynecology",
  "maternal-fetal-medicine",
  "emergency-medicine",
  "acute-care",
  "urgent-care",
  "general-surgery",
  "orthopedic-surgery",
  "urology",
  "ophthalmology",
  "anesthesiology",
  "dermatology",
  "otolaryngology",
  "plastic-surgery",
  "endocrinology",
  "gastroenterology",
  "nephrology",
  "rheumatology",
  "infectious-disease",
] as const;

export type AudienceSlug = (typeof AUDIENCE_SLUGS)[number];

export interface AudienceConfig {
  slug: AudienceSlug;
  parentToken: string;
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string;
  heroLabel: string;
  heroTitle: string;
  heroSubtitle: string;
  badgePersonaId: PersonaId;
  letterExample: string;
  detail: {
    summary: string;
  };
  ctaTitle: string;
  ctaSubtitle: string;
  opportunity: OpportunityListingCard;
}

const baseOpportunity = {
  studyId: null,
  shortDescription: null,
  tags: null,
  payRateFrequency: "HOURLY",
  workMode: "REMOTE",
  opportunityType: "INTERVIEW",
  referralBonusAmount: null,
  referralCommissionBps: 0,
  // A hiring category, not a study: no organization's setting reaches it.
  referralsEnabled: true,
  publishedAt: null,
} as const;

const AUDIENCES: AudienceConfig[] = [
  {
    slug: "finance",
    parentToken: "finance",
    metaTitle: "Finance Experts: Get Paid $60-$160/hr to Evaluate AI",
    metaDescription:
      "FP&A, tax, audit, treasury, and credit risk experts earn $60-$160/hr reviewing AI output. Remote, hourly, on your schedule. A few hours a week.",
    heroEyebrow: "Finance Network",
    heroLabel: "finance and financial analysis work",
    heroTitle: "Your financial judgment, now worth $60-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the forecasts, valuations, and risk calls you already make. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "an FP&A analyst's eye on a forecast model",
    detail: {
      summary:
        "Review a model's forecasts and investment memos with the scrutiny you give an analyst on your team. The judgment from years of closing books and defending forecasts is what AI labs pay for.",
    },
    ctaTitle: "Ready to put your finance work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs, hedge funds, and investment teams that need real finance experience.",
    opportunity: {
      ...baseOpportunity,
      id: "finance",
      token: "finance",
      title: "Finance",
      payRateMin: "60",
      payRateMax: "160",
      requirements: [
        "FP&A, Treasury, Tax, Audit, or Credit Risk Analyst (US, 5-10 yrs)",
        "AP/AR, Corp Dev, Banking, Compliance, or Ops Analyst",
      ],
      referralTitle: "Finance",
      spotsRemaining: 48,
    },
  },
  {
    slug: "accounting",
    parentToken: "accounting",
    metaTitle: "Accounting Experts: $50-$140/hr, Remote, Paid Hourly",
    metaDescription:
      "Accountants, auditors, and tax pros earn $50-$140/hr reviewing AI work remotely. A few hours a week, paid hourly, on your schedule.",
    heroEyebrow: "Accounting Network",
    heroLabel: "accounting and finance work",
    heroTitle: "Your accounting judgment, now worth $50-$140 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the closes, audits, and tax positions you already handle. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a senior auditor's eye on a SOX walkthrough",
    detail: {
      summary:
        "You review AI-drafted tax positions, reconciliations, and close workflows the way you'd review a junior's work, flagging what's wrong and defending what's right. A few hours a week, remote, paid hourly.",
    },
    ctaTitle: "Ready to put your accounting work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote projects from AI labs and fintechs that need real accounting reps. Paid hourly, on your schedule.",
    opportunity: {
      ...baseOpportunity,
      id: "accounting",
      token: "accounting",
      title: "Accounting",
      payRateMin: "50",
      payRateMax: "140",
      requirements: [
        "Staff, Senior, Tax, Cost, or Grant Accountant (US, 5-10 yrs)",
        "Audit Associate, EBP Senior, CAS, SOX / Internal Audit",
      ],
      referralTitle: "Accounting",
      spotsRemaining: 62,
    },
  },
  {
    slug: "healthcare-ops",
    parentToken: "healthcare-ops",
    metaTitle: "Healthcare Ops Experts: $50-$120/hr Reviewing AI",
    metaDescription:
      "Medical coders, CDI specialists, revenue cycle pros, and UM nurses earn $50-$120/hr, remote, evaluating healthcare AI a few hours a week. Paid hourly.",
    heroEyebrow: "Healthcare Ops Network",
    heroLabel: "healthcare operations work",
    heroTitle: "Your healthcare ops judgment, $50-$120 an hour.",
    heroSubtitle:
      "Coders, CDI specialists, revenue cycle pros, and UM nurses, pressure-test medical AI a few remote hours a week. Paid hourly. No patients, no live claims.",
    badgePersonaId: "doctor",
    letterExample: "a CDI specialist's eye on a medical record review",
    detail: {
      summary:
        "Review AI-drafted denials, prior auths, and utilization decisions, flagging what a flowchart misses: the upcode, the unsupported diagnosis, the wrongful denial. Remote, a few hours a week, paid hourly on your schedule.",
    },
    ctaTitle: "Ready to put your healthcare ops work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and healthtech that need real coding, revenue cycle, and care management reps.",
    opportunity: {
      ...baseOpportunity,
      id: "healthcare-ops",
      token: "healthcare-ops",
      title: "Healthcare Ops",
      payRateMin: "50",
      payRateMax: "120",
      requirements: [
        "Medical Coder, CDI Specialist, or Revenue Cycle Analyst (US, 5-10 yrs)",
        "UM Nurse, Case Manager, Quality, Compliance, or Risk Coordinator",
      ],
      referralTitle: "Healthcare Ops",
      spotsRemaining: 71,
    },
  },
  {
    slug: "software-engineering",
    parentToken: "swe",
    metaTitle: "Software Engineers: Get Paid $60-$200/hr to Review AI Code",
    metaDescription:
      "Senior software, backend, frontend, ML, DevOps, SRE, and data engineers get paid $60-$200/hr to catch the bugs in AI code that compiles but is wrong. Remote, hourly, your schedule.",
    heroEyebrow: "Engineering Network",
    heroLabel: "software engineering work",
    heroTitle: "Your code review, now worth $60-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI code the way you review a teammate's PR. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "a senior engineer's eye on a tricky code review",
    detail: {
      summary:
        "Review AI pull requests for the bug that compiles but is wrong, the query that kills prod at scale, the abstraction every senior would flag. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your engineering work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and tooling teams that need real production engineering, not leetcode.",
    opportunity: {
      ...baseOpportunity,
      id: "swe",
      token: "swe",
      title: "Software Engineering",
      payRateMin: "60",
      payRateMax: "200",
      requirements: [
        "Full Stack, Backend, Frontend, Mobile, or ML Engineer (US/UK/CA, 2+ yrs)",
        "DevOps / Platform, SRE, or Data Engineer",
      ],
      referralTitle: "Software Engineering",
      spotsRemaining: 134,
    },
  },
  {
    slug: "management-consultants",
    parentToken: "consultants",
    metaTitle: "Management Consultants: Get Paid $90-$200/hr to Review AI",
    metaDescription:
      "Management consultants earn $90-$200/hr, remote, reviewing AI work on strategy, operations, and decks. A few hours a week, paid per task, on your schedule.",
    heroEyebrow: "Consulting Network",
    heroLabel: "consulting and strategy work",
    heroTitle: "Your consulting judgment, now worth $90-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the cases, frameworks, and decks you already run. Paid per task, on your schedule.",
    badgePersonaId: "consultant",
    letterExample: "a former MBB consultant's eye on a market-entry case",
    detail: {
      summary:
        "Run a model through a market-entry case the way you would a candidate, then mark its decks for the so-what that is missing and the cost logic that breaks. Your reasoning is the benchmark.",
    },
    ctaTitle: "Ready to put your consulting work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and strategy teams that need real reps, not textbook frameworks.",
    opportunity: {
      ...baseOpportunity,
      id: "consultants",
      token: "consultants",
      title: "Management Consultants",
      payRateMin: "90",
      payRateMax: "200",
      requirements: [
        "5+ years at a top strategy or ops consulting firm",
        "Experience advising Fortune 500 leadership",
      ],
      referralTitle: "Management Consultants",
      spotsRemaining: 104,
    },
  },
  {
    slug: "product-experts",
    parentToken: "platform-experts",
    metaTitle: "Product & Platform Experts: $70-$160/hr Evaluating AI",
    metaDescription:
      "Senior PMs and platform product leaders earn $70-$160/hr reviewing AI product work. Remote, a few hours a week, paid per task on your schedule.",
    heroEyebrow: "Product Network",
    heroLabel: "product and platform work",
    heroTitle: "Your product judgment, now worth $70-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the specs, roadmaps, and platform calls you already make. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a senior PM's eye on a real product spec",
    detail: {
      summary:
        "Review AI-drafted PRDs, RFCs, and strategy docs against the bar a staff PM would hold. Your taste for prioritization, constraints, and execution is the asset. Remote and paid hourly.",
    },
    ctaTitle: "Put your product work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and dev tool companies that need real product and platform reps.",
    opportunity: {
      ...baseOpportunity,
      id: "platform-experts",
      token: "platform-experts",
      title: "Product / Platform Experts",
      payRateMin: "70",
      payRateMax: "160",
      requirements: [
        "Senior PM or platform engineer at a top tech company",
        "Shipped infra, dev tools, or developer-facing products",
      ],
      referralTitle: "Product / Platform Experts",
      spotsRemaining: 120,
    },
  },
  {
    slug: "data-scientists",
    parentToken: "data-scientists",
    metaTitle: "Data Scientists: Get Paid $70-$180/hr to Review AI Analysis",
    metaDescription:
      "Data scientists, ML engineers, and statisticians get paid $70-$180/hr to catch the data leakage and p-hacking in AI notebooks and model evals. Remote, hourly, your schedule.",
    heroEyebrow: "Data Science Network",
    heroLabel: "data science and machine learning work",
    heroTitle: "Your modeling judgment, now worth $70-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI notebooks and evals the way you review a teammate's PR. Paid hourly, on your schedule.",
    badgePersonaId: "scientist",
    letterExample: "a data scientist's eye on a leaky validation set",
    detail: {
      summary:
        "Review AI notebooks and model evals for the data leakage, the p-hacking, the silently wrong baseline. The instinct for what makes a result trustworthy. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your data science work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and ML platforms that need real modeling judgment, not Kaggle theory.",
    opportunity: {
      ...baseOpportunity,
      id: "data-scientists",
      token: "data-scientists",
      title: "Data Science",
      payRateMin: "70",
      payRateMax: "180",
      requirements: [
        "Data Scientist, ML Engineer, or Applied Scientist (US/UK/CA, 3+ yrs)",
        "Statistician, Analytics Lead, or Research Scientist",
      ],
      referralTitle: "Data Science",
      spotsRemaining: 96,
    },
  },
  {
    slug: "physicians",
    parentToken: "physicians",
    metaTitle: "Physician Experts: $100-$300/hr, Remote, Reviewing Medical AI",
    metaDescription:
      "Board-certified physicians earn $100-$300/hr, remote, a few hours a week, checking a model's diagnoses and treatment plans. Paid per task. No patients, no liability.",
    heroEyebrow: "Physician Network",
    heroLabel: "clinical and medical work",
    heroTitle: "Your clinical judgment, now worth $100-$300 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the differentials, treatment calls, and charts a real attending would not sign. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an attending's eye on a missed differential",
    detail: {
      summary:
        "Review AI-drafted diagnoses, treatment plans, and clinical notes the way you'd check a resident's work: flag the dangerous call, defend the standard of care. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your clinical work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need real bedside judgment, not memorized guidelines.",
    opportunity: {
      ...baseOpportunity,
      id: "physicians",
      token: "physicians",
      title: "Physicians",
      payRateMin: "100",
      payRateMax: "300",
      requirements: [
        "Board-certified physician or specialist (US, active or recent practice)",
        "Hospitalist, attending, or fellowship-trained MD/DO",
      ],
      referralTitle: "Physicians",
      spotsRemaining: 38,
    },
  },
  {
    slug: "nurses",
    parentToken: "nurses",
    metaTitle: "Nurse Experts: $60-$150/hr, Remote, Reviewing Healthcare AI",
    metaDescription:
      "RNs and nurse practitioners earn $60-$150/hr, remote, a few hours a week, checking a model's care plans, triage, and patient education. Paid per task. No patients, no liability.",
    heroEyebrow: "Nursing Network",
    heroLabel: "nursing and clinical care work",
    heroTitle: "Your bedside judgment, now worth $60-$150 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing healthcare AI - the triage calls, care plans, and deterioration you catch before the monitor does. Paid per task. No liability.",
    badgePersonaId: "doctor",
    letterExample: "an ICU nurse's eye on a triage call",
    detail: {
      summary:
        "Review AI-drafted care plans, triage decisions, and discharge instructions the way you'd check a new grad's charting: flag what's unsafe, defend what works on a real floor. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your nursing work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need real bedside nursing, not protocols on paper.",
    opportunity: {
      ...baseOpportunity,
      id: "nurses",
      token: "nurses",
      title: "Nursing",
      payRateMin: "60",
      payRateMax: "150",
      requirements: [
        "RN, NP, or clinical nurse specialist (US, active license, 3+ yrs)",
        "ICU, ED, med-surg, oncology, or perioperative experience",
      ],
      referralTitle: "Nursing",
      spotsRemaining: 84,
    },
  },
  {
    slug: "lawyers",
    parentToken: "lawyers",
    metaTitle: "Lawyer Experts: $90-$250/hr Reviewing AI Legal Work",
    metaDescription:
      "Attorneys, litigators, and corporate counsel earn $90-$250/hr remotely. A few hours a week stress-testing AI on contracts, briefs, and legal reasoning. Paid hourly, on your schedule.",
    heroEyebrow: "Legal Network",
    heroLabel: "legal and counsel work",
    heroTitle: "Your legal judgment, now worth $90-$250 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the clauses you would never sign and the arguments that lose on appeal. Paid hourly, on your schedule.",
    badgePersonaId: "lawyer",
    letterExample: "a litigator's eye on a flawed argument",
    detail: {
      summary:
        "Review a model's contracts and briefs the way you would review a first-year associate's draft. Catch the clause that creates liability, the misread holding, the citation that does not exist.",
    },
    ctaTitle: "Ready to put your legal work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and legaltech teams that need real practice, not bar-exam answers.",
    opportunity: {
      ...baseOpportunity,
      id: "lawyers",
      token: "lawyers",
      title: "Lawyers",
      payRateMin: "90",
      payRateMax: "250",
      requirements: [
        "Licensed attorney (US/UK, 4+ yrs in practice)",
        "Litigation, corporate, IP, regulatory, or transactional focus",
      ],
      referralTitle: "Lawyers",
      spotsRemaining: 57,
    },
  },
  {
    slug: "mechanical-engineers",
    parentToken: "mechanical-engineers",
    metaTitle: "Mechanical Engineers: $60-$160/hr Reviewing AI Design",
    metaDescription:
      "Mechanical, manufacturing, and design engineers earn $60-$160/hr, remote, pressure-testing AI on CAD, tolerances, and FEA. Paid per task, on your schedule.",
    heroEyebrow: "Mechanical Engineering Network",
    heroLabel: "mechanical engineering work",
    heroTitle: "Your engineering judgment, now worth $60-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the AI errors only an ME spots. Paid per task, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "a mechanical engineer's eye on a tolerance stack-up",
    detail: {
      summary:
        "You review AI-drafted designs, FMEAs, and tolerance analyses the way you would a junior engineer's package, flagging the tolerance that cannot be held and the part that fails under fatigue. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your engineering judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and hardware teams that need real mechanical design experience.",
    opportunity: {
      ...baseOpportunity,
      id: "mechanical-engineers",
      token: "mechanical-engineers",
      title: "Mechanical Engineering",
      payRateMin: "60",
      payRateMax: "160",
      requirements: [
        "Mechanical, manufacturing, or design engineer (US, 4+ yrs)",
        "CAD, FEA, GD&T, or DFM experience",
      ],
      referralTitle: "Mechanical Engineering",
      spotsRemaining: 73,
    },
  },
  {
    slug: "electrical-engineers",
    parentToken: "electrical-engineers",
    metaTitle: "Electrical Engineers: $65-$170/hr Reviewing AI Hardware",
    metaDescription:
      "Electrical, hardware, and embedded engineers earn $65-$170/hr, remote, pressure-testing AI on schematics, firmware, and circuit design. Paid hourly, on your schedule.",
    heroEyebrow: "Electrical Engineering Network",
    heroLabel: "electrical and hardware engineering work",
    heroTitle: "Your engineering judgment, now worth $65-$170 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the schematic and firmware errors only an EE spots. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "a hardware engineer's eye on a noisy power rail",
    detail: {
      summary:
        "You review AI-drafted schematics, firmware, and circuit analysis the way you would a board before tape-out, catching the grounding mistake and the part that browns out at temperature. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your engineering judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and hardware teams that need real electrical design experience.",
    opportunity: {
      ...baseOpportunity,
      id: "electrical-engineers",
      token: "electrical-engineers",
      title: "Electrical Engineering",
      payRateMin: "65",
      payRateMax: "170",
      requirements: [
        "Electrical, hardware, or embedded engineer (US, 4+ yrs)",
        "PCB design, firmware, RF, power, or signal integrity experience",
      ],
      referralTitle: "Electrical Engineering",
      spotsRemaining: 61,
    },
  },
  {
    slug: "civil-engineers",
    parentToken: "civil-engineers",
    metaTitle: "Civil & Structural Engineers: $60-$150/hr Reviewing AI",
    metaDescription:
      "Civil, structural, and geotechnical engineers earn $60-$150/hr, remote, pressure-testing AI on load calcs, codes, and structural design. Paid hourly, on your schedule.",
    heroEyebrow: "Civil Engineering Network",
    heroLabel: "civil and structural engineering work",
    heroTitle: "Your engineering judgment, now worth $60-$150 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the load-path and code errors only a structural engineer spots. Paid hourly, on your schedule.",
    badgePersonaId: "construction",
    letterExample: "a structural engineer's eye on an under-sized beam",
    detail: {
      summary:
        "You review AI-drafted structural calcs, code analyses, and drawings the way you would before stamping, catching the load path that does not close and the code section misapplied. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your engineering judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and infrastructure teams that need real structural judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "civil-engineers",
      token: "civil-engineers",
      title: "Civil Engineering",
      payRateMin: "60",
      payRateMax: "150",
      requirements: [
        "PE or EIT in civil, structural, or geotechnical (US, 4+ yrs)",
        "Building codes, load analysis, or site design experience",
      ],
      referralTitle: "Civil Engineering",
      spotsRemaining: 52,
    },
  },
  {
    slug: "cybersecurity",
    parentToken: "cybersecurity",
    metaTitle:
      "Cybersecurity Experts: Get Paid $70-$200/hr to Review AI Security",
    metaDescription:
      "Security engineers, penetration testers, and SOC analysts get paid $70-$200/hr to catch the security holes AI misses in exploits, defenses, and threat analysis. Remote, hourly, your schedule.",
    heroEyebrow: "Security Network",
    heroLabel: "cybersecurity and offensive security work",
    heroTitle: "Your security judgment, now worth $70-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI exploits and defenses the way you review a red-team report. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "a pentester's eye on a subtle auth bypass",
    detail: {
      summary:
        "Review AI exploits and detections: confirm what pops a box, flag the detection that misses, walk the kill chain a model glosses over. Your sense for how systems get owned. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your security work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and security teams that need real offensive and defensive reps, not CTF write-ups.",
    opportunity: {
      ...baseOpportunity,
      id: "cybersecurity",
      token: "cybersecurity",
      title: "Cybersecurity",
      payRateMin: "70",
      payRateMax: "200",
      requirements: [
        "Security engineer, pentester, or SOC analyst (US/UK/CA, 3+ yrs)",
        "Offensive security, detection engineering, or incident response experience",
      ],
      referralTitle: "Cybersecurity",
      spotsRemaining: 88,
    },
  },
  {
    slug: "economists",
    parentToken: "economists",
    metaTitle: "Economist Experts: $80-$200/hr Reviewing AI Analysis",
    metaDescription:
      "Economists, econometricians, and policy analysts earn $80-$200/hr remotely. A few hours a week pressure-testing AI on models, forecasts, and identification. Paid hourly, on your schedule.",
    heroEyebrow: "Economics Network",
    heroLabel: "economics and policy work",
    heroTitle: "Your econometric judgment, now worth $80-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week refereeing AI on the forecasts you stand behind and the assumptions you would never wave through. Paid hourly, on your schedule.",
    badgePersonaId: "researcher",
    letterExample: "an economist's eye on a shaky identification strategy",
    detail: {
      summary:
        "Referee a model's analysis the way you would a working paper. Flag the endogeneity, the unidentified model, the conclusion the data will not support. Your sense of what a number actually means is the work.",
    },
    ctaTitle: "Ready to put your economics work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and policy groups that need real applied economics, not textbook models.",
    opportunity: {
      ...baseOpportunity,
      id: "economists",
      token: "economists",
      title: "Economics",
      payRateMin: "80",
      payRateMax: "200",
      requirements: [
        "PhD or MA economist, or applied economics professional (3+ yrs)",
        "Econometrics, forecasting, or policy analysis experience",
      ],
      referralTitle: "Economics",
      spotsRemaining: 44,
    },
  },
  {
    slug: "marketing-experts",
    parentToken: "marketing-experts",
    metaTitle: "Marketing Experts: $50-$140/hr Reviewing AI Campaigns",
    metaDescription:
      "Growth, brand, performance, and SEO marketers earn $50-$140/hr, remote, pressure-testing AI on campaigns, copy, and strategy. Paid hourly, on your schedule.",
    heroEyebrow: "Marketing Network",
    heroLabel: "marketing and growth work",
    heroTitle: "Your marketing judgment, now worth $50-$140 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the campaign and positioning errors only a working marketer spots. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a growth lead's eye on a campaign that won't convert",
    detail: {
      summary:
        "You review AI-drafted campaigns, copy, and go-to-market strategy the way you would a brief from your team, flagging the positioning that misses and the channel that will not scale. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your marketing judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and growth teams that need real marketing reps.",
    opportunity: {
      ...baseOpportunity,
      id: "marketing-experts",
      token: "marketing-experts",
      title: "Marketing",
      payRateMin: "50",
      payRateMax: "140",
      requirements: [
        "Growth, brand, performance, or lifecycle marketer (US/UK/CA, 4+ yrs)",
        "Scaled paid, SEO, content, or product marketing",
      ],
      referralTitle: "Marketing",
      spotsRemaining: 110,
    },
  },
  {
    slug: "sales-experts",
    parentToken: "sales-experts",
    metaTitle: "Sales Experts: $50-$150/hr Reviewing AI Deal Strategy",
    metaDescription:
      "Enterprise sellers, sales engineers, and RevOps leaders earn $50-$150/hr, remote, pressure-testing AI on outreach, discovery, and deal strategy. Paid hourly, on your schedule.",
    heroEyebrow: "Sales Network",
    heroLabel: "sales and revenue work",
    heroTitle: "Your sales judgment, now worth $50-$150 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the talk tracks and discovery moves only a seasoned seller spots. Paid hourly, on your schedule.",
    badgePersonaId: "consultant",
    letterExample:
      "an enterprise AE's eye on a discovery call that went nowhere",
    detail: {
      summary:
        "You review AI-drafted outreach, discovery, and deal strategy the way you would coach a rep, flagging the email that gets ignored and the close that pushes too soon. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your sales judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and revenue teams that need real selling reps.",
    opportunity: {
      ...baseOpportunity,
      id: "sales-experts",
      token: "sales-experts",
      title: "Sales",
      payRateMin: "50",
      payRateMax: "150",
      requirements: [
        "Enterprise AE, sales engineer, or RevOps leader (US, 4+ yrs)",
        "Closed complex B2B deals or built a sales motion",
      ],
      referralTitle: "Sales",
      spotsRemaining: 97,
    },
  },
  {
    slug: "ux-designers",
    parentToken: "ux-designers",
    metaTitle: "Product & UX Designers: $60-$160/hr Evaluating AI",
    metaDescription:
      "Product designers and UX researchers earn $60-$160/hr reviewing AI-drafted interfaces, flows, and design critique. Remote, a few hours a week, paid per task.",
    heroEyebrow: "Design Network",
    heroLabel: "product and UX design work",
    heroTitle: "Your design judgment, now worth $60-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the flows you've shipped and the critiques you give. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a design lead's eye on a broken onboarding flow",
    detail: {
      summary:
        "Review AI-drafted interfaces, flows, and design rationale the way you'd run a crit. Flag the pattern that breaks accessibility and the flow that adds friction. Remote and paid hourly.",
    },
    ctaTitle: "Put your design work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and product teams that need real design judgment, not portfolio shots.",
    opportunity: {
      ...baseOpportunity,
      id: "ux-designers",
      token: "ux-designers",
      title: "Product & UX Design",
      payRateMin: "60",
      payRateMax: "160",
      requirements: [
        "Product designer, UX researcher, or design lead (US/UK/CA, 4+ yrs)",
        "Shipped consumer or enterprise products end to end",
      ],
      referralTitle: "Product & UX Design",
      spotsRemaining: 78,
    },
  },
  {
    slug: "pharmacists",
    parentToken: "pharmacists",
    metaTitle: "Pharmacist Experts: $60-$150/hr, Remote, Reviewing Medical AI",
    metaDescription:
      "Pharmacists earn $60-$150/hr, remote, a few hours a week, checking a model's dosing, interactions, and therapy decisions. Paid per task. No patients, no liability.",
    heroEyebrow: "Pharmacy Network",
    heroLabel: "pharmacy and medication therapy work",
    heroTitle: "Your dosing and interaction calls, now worth $60-$150 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the interactions, dose adjustments, and scripts you'd call the doctor about. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a clinical pharmacist's eye on a dangerous interaction",
    detail: {
      summary:
        "Review AI-drafted dosing, interaction checks, and therapy plans the way you'd verify an order before it reaches a patient: catch the contraindication, the renal-dose miss, the duplicate therapy. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your pharmacy work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need real pharmacy judgment, not drug-reference lookups.",
    opportunity: {
      ...baseOpportunity,
      id: "pharmacists",
      token: "pharmacists",
      title: "Pharmacy",
      payRateMin: "60",
      payRateMax: "150",
      requirements: [
        "PharmD or RPh (US, active license, 3+ yrs)",
        "Clinical, hospital, retail, or specialty pharmacy experience",
      ],
      referralTitle: "Pharmacy",
      spotsRemaining: 49,
    },
  },
  {
    slug: "mental-health",
    parentToken: "mental-health",
    metaTitle: "Mental Health Experts: $60-$160/hr, Remote, Reviewing AI",
    metaDescription:
      "Psychologists and licensed clinicians earn $60-$160/hr, remote, a few hours a week, checking a model's risk assessments and therapeutic responses. Paid per task. No patients, no liability.",
    heroEyebrow: "Mental Health Network",
    heroLabel: "clinical psychology and therapy work",
    heroTitle: "Your clinical judgment, now worth $60-$160 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing AI - the risk you assess, the rapport you protect, the response that helps versus harms. Paid per task. No liability.",
    badgePersonaId: "doctor",
    letterExample: "a therapist's eye on a missed risk cue",
    detail: {
      summary:
        "Review AI-drafted therapeutic responses, risk assessments, and clinical reasoning the way you'd supervise a trainee: catch the missed suicidality cue, the rapport-rupturing reply, the boundary that can't be crossed. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your clinical work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need real clinical judgment, not scripted empathy.",
    opportunity: {
      ...baseOpportunity,
      id: "mental-health",
      token: "mental-health",
      title: "Mental Health",
      payRateMin: "60",
      payRateMax: "160",
      requirements: [
        "Licensed psychologist, therapist, or counselor (US, 3+ yrs)",
        "Clinical assessment, risk, or psychotherapy experience",
      ],
      referralTitle: "Mental Health",
      spotsRemaining: 66,
    },
  },
  {
    slug: "academics",
    parentToken: "academics",
    metaTitle: "Academic & PhD Experts: $70-$200/hr Reviewing AI Research",
    metaDescription:
      "Professors, postdocs, and PhDs earn $70-$200/hr remotely. A few hours a week refereeing AI on research, proofs, and domain reasoning. Paid hourly, on your schedule.",
    heroEyebrow: "Academic Network",
    heroLabel: "academic and research work",
    heroTitle: "Your research rigor, now worth $70-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week refereeing AI on the proofs you check and the claims you would never let past peer review. Paid hourly, on your schedule.",
    badgePersonaId: "researcher",
    letterExample: "a referee's eye on an overstated result",
    detail: {
      summary:
        "Referee a model's research and proofs the way you would a submission. Catch the unsupported leap, the misattributed citation, the result that will not replicate. Deep depth in your narrow field is the signal.",
    },
    ctaTitle: "Ready to put your research on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs that need real domain depth, not survey-level knowledge.",
    opportunity: {
      ...baseOpportunity,
      id: "academics",
      token: "academics",
      title: "Academia & Research",
      payRateMin: "70",
      payRateMax: "200",
      requirements: [
        "PhD, postdoc, or faculty in any field (or ABD with publications)",
        "Peer-reviewed publications or equivalent research output",
      ],
      referralTitle: "Academia & Research",
      spotsRemaining: 92,
    },
  },
  {
    slug: "writers-editors",
    parentToken: "writers-editors",
    metaTitle: "Writer & Editor Experts: $40-$120/hr Reviewing AI Prose",
    metaDescription:
      "Professional writers, editors, and journalists earn $40-$120/hr remotely. A few hours a week editing AI-drafted writing and fact-checking. Paid hourly, on your schedule.",
    heroEyebrow: "Writing Network",
    heroLabel: "writing and editing work",
    heroTitle: "Your editorial judgment, now worth $40-$120 an hour.",
    heroSubtitle:
      "A few remote hours a week editing AI-drafted prose for the buried lede, the fabricated quote, the sentence you would cut without mercy. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "an editor's eye on a buried lede",
    detail: {
      summary:
        "Edit a model's writing the way you would a staff draft. Flag the buried lede, the fabricated quote, the prose that is fluent but says nothing. Your ear for what is worth reading is the work.",
    },
    ctaTitle: "Ready to put your writing on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs that need real editorial judgment, not grammar checks.",
    opportunity: {
      ...baseOpportunity,
      id: "writers-editors",
      token: "writers-editors",
      title: "Writing & Editing",
      payRateMin: "40",
      payRateMax: "120",
      requirements: [
        "Professional writer, editor, or journalist (4+ yrs)",
        "Published bylines, edited publications, or equivalent body of work",
      ],
      referralTitle: "Writing & Editing",
      spotsRemaining: 130,
    },
  },
  {
    slug: "translators",
    parentToken: "translators",
    metaTitle: "Translator & Linguist Experts: $40-$110/hr Reviewing AI Output",
    metaDescription:
      "Professional translators, interpreters, and linguists earn $40-$110/hr remotely. A few hours a week reviewing AI translation and localization. Paid hourly, on your schedule.",
    heroEyebrow: "Linguistics Network",
    heroLabel: "translation and linguistics work",
    heroTitle: "Your fluency and nuance, now worth $40-$110 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI translation for the idiom it botches and the register it misses. Paid hourly, on your schedule.",
    badgePersonaId: "researcher",
    letterExample:
      "a translator's eye on a literal rendering that loses the meaning",
    detail: {
      summary:
        "Review a model's translation the way you would a colleague's work before delivery. Flag the botched idiom, the wrong register, the cultural reference that lands flat. You know what the words should become, not just what they mean.",
    },
    ctaTitle: "Ready to put your translation work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs that need real native fluency and localization judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "translators",
      token: "translators",
      title: "Translation & Linguistics",
      payRateMin: "40",
      payRateMax: "110",
      requirements: [
        "Professional translator, interpreter, or linguist (3+ yrs)",
        "Native or near-native fluency in a non-English language",
      ],
      referralTitle: "Translation & Linguistics",
      spotsRemaining: 105,
    },
  },
  {
    slug: "actuaries",
    parentToken: "actuaries",
    metaTitle: "Actuary Experts: Get Paid $80-$200/hr to Evaluate AI",
    metaDescription:
      "Actuaries and risk modelers earn $80-$200/hr reviewing AI pricing, reserving, and risk models. Remote, hourly, on your schedule.",
    heroEyebrow: "Actuarial Network",
    heroLabel: "actuarial and risk modeling work",
    heroTitle: "Your actuarial judgment, now worth $80-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the reserves, assumptions, and pricing models you'd sign an opinion on. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "an actuary's eye on an under-reserved book",
    detail: {
      summary:
        "Review a model's pricing and reserving the way you'd review work before signing an actuarial opinion. Flag the assumption that won't hold, the reserve that's light, the model that misprices the tail.",
    },
    ctaTitle: "Ready to put your actuarial work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and insurers that need real actuarial judgment, not exam syllabus answers.",
    opportunity: {
      ...baseOpportunity,
      id: "actuaries",
      token: "actuaries",
      title: "Actuarial",
      payRateMin: "80",
      payRateMax: "200",
      requirements: [
        "ASA, FSA, ACAS, FCAS, or actuarial analyst (US, 3+ yrs)",
        "Pricing, reserving, or risk modeling experience",
      ],
      referralTitle: "Actuarial",
      spotsRemaining: 40,
    },
  },
  {
    slug: "quant-finance",
    parentToken: "quant-finance",
    metaTitle: "Quant Experts: Get Paid $100-$300/hr to Evaluate AI",
    metaDescription:
      "Quant researchers, traders, and developers earn $100-$300/hr reviewing AI models, strategies, and derivations. Remote, hourly, on your schedule.",
    heroEyebrow: "Quant Network",
    heroLabel: "quantitative finance and trading work",
    heroTitle: "Your quant judgment, now worth $100-$300 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the signals, models, and strategies you'd put capital behind. Paid hourly, on your schedule.",
    badgePersonaId: "scientist",
    letterExample: "a quant's eye on an overfit backtest",
    detail: {
      summary:
        "Review a model's quant models and derivations the way you'd vet research before it touches capital. Flag the overfit backtest, the look-ahead bias, the derivation that breaks under real assumptions.",
    },
    ctaTitle: "Ready to put your quant work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and funds that need real quant judgment, not textbook stochastic calculus.",
    opportunity: {
      ...baseOpportunity,
      id: "quant-finance",
      token: "quant-finance",
      title: "Quantitative Finance",
      payRateMin: "100",
      payRateMax: "300",
      requirements: [
        "Quant researcher, trader, or developer at a fund/bank/desk (3+ yrs)",
        "Strong math/stats and production modeling experience",
      ],
      referralTitle: "Quantitative Finance",
      spotsRemaining: 36,
    },
  },
  {
    slug: "biology-life-sciences",
    parentToken: "biology-life-sciences",
    metaTitle: "Biology & Life Sciences: $70-$180/hr Reviewing AI",
    metaDescription:
      "Biologists, biotech scientists, and bioinformaticians earn $70-$180/hr, remote, pressure-testing AI on experimental design and analysis. Paid hourly, on your schedule.",
    heroEyebrow: "Life Sciences Network",
    heroLabel: "biology and life sciences work",
    heroTitle: "Your scientific judgment, now worth $70-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the experimental and analysis errors only a bench scientist spots. Paid hourly, on your schedule.",
    badgePersonaId: "scientist",
    letterExample: "a biologist's eye on a missing control",
    detail: {
      summary:
        "You review AI-drafted experimental designs, analyses, and claims the way you would a labmate's protocol, flagging the missing control and the conclusion the assay cannot support. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your scientific judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and biotech teams that need real bench and analysis experience.",
    opportunity: {
      ...baseOpportunity,
      id: "biology-life-sciences",
      token: "biology-life-sciences",
      title: "Biology & Life Sciences",
      payRateMin: "70",
      payRateMax: "180",
      requirements: [
        "PhD, MS, or industry scientist in biology, biotech, or bioinformatics (3+ yrs)",
        "Wet-lab, computational, or translational research experience",
      ],
      referralTitle: "Biology & Life Sciences",
      spotsRemaining: 58,
    },
  },
  {
    slug: "chemistry",
    parentToken: "chemistry",
    metaTitle: "Chemistry Experts: $70-$180/hr Reviewing AI Chemistry",
    metaDescription:
      "Chemists and materials scientists earn $70-$180/hr, remote, pressure-testing AI on synthesis, mechanisms, and spectra. Paid per task, on your schedule.",
    heroEyebrow: "Chemistry Network",
    heroLabel: "chemistry and materials science work",
    heroTitle: "Your chemistry judgment, now worth $70-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the synthesis and mechanism errors only a chemist spots. Paid per task, on your schedule.",
    badgePersonaId: "scientist",
    letterExample: "a chemist's eye on a synthesis route that won't work",
    detail: {
      summary:
        "You review AI-drafted syntheses, mechanisms, and analyses the way you would a route before running it, flagging the step that will not proceed and the hazard a model ignores. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your chemistry judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and chemicals teams that need real bench chemistry.",
    opportunity: {
      ...baseOpportunity,
      id: "chemistry",
      token: "chemistry",
      title: "Chemistry",
      payRateMin: "70",
      payRateMax: "180",
      requirements: [
        "PhD, MS, or industry chemist (organic, analytical, or materials, 3+ yrs)",
        "Synthesis, characterization, or process chemistry experience",
      ],
      referralTitle: "Chemistry",
      spotsRemaining: 47,
    },
  },
  {
    slug: "supply-chain",
    parentToken: "supply-chain",
    metaTitle: "Supply Chain & Ops Experts: $50-$140/hr Reviewing AI",
    metaDescription:
      "Supply chain, logistics, and operations leaders earn $50-$140/hr, remote, pressure-testing AI on planning, forecasting, and ops strategy. Paid hourly, on your schedule.",
    heroEyebrow: "Supply Chain Network",
    heroLabel: "supply chain and operations work",
    heroTitle: "Your operations judgment, now worth $50-$140 an hour.",
    heroSubtitle:
      "A few remote hours a week catching the planning and logistics errors only an operator spots. Paid hourly, on your schedule.",
    badgePersonaId: "consultant",
    letterExample: "a planner's eye on a forecast that ignores lead time",
    detail: {
      summary:
        "You review AI-drafted demand plans, logistics strategy, and ops decisions the way you would a plan before committing, flagging the forecast that ignores lead time and the inventory call that strands cash. Remote, a few hours a week, paid hourly.",
    },
    ctaTitle: "Put your operations judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and operations teams that need real supply chain reps.",
    opportunity: {
      ...baseOpportunity,
      id: "supply-chain",
      token: "supply-chain",
      title: "Supply Chain & Operations",
      payRateMin: "50",
      payRateMax: "140",
      requirements: [
        "Supply chain, logistics, procurement, or ops leader (US, 4+ yrs)",
        "Demand planning, fulfillment, or operations management experience",
      ],
      referralTitle: "Supply Chain & Operations",
      spotsRemaining: 69,
    },
  },
  {
    slug: "fpa-analysts",
    parentToken: "fpa-analysts",
    metaTitle: "FP&A Analysts: Get Paid $65-$150/hr to Evaluate AI",
    metaDescription:
      "FP&A analysts and finance managers earn $65-$150/hr reviewing AI forecasts, models, and variance analysis. Remote, hourly, on your schedule.",
    heroEyebrow: "FP&A Network",
    heroLabel: "FP&A and financial planning work",
    heroTitle: "Your FP&A judgment, now worth $65-$150 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the forecasts, operating models, and board decks you already build. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "an FP&A analyst's eye on a forecast that doesn't tie out",
    detail: {
      summary:
        "Review a model's forecasts and operating models the way you'd review a draft before it goes to the CFO. Flag the assumption that breaks the model and the driver that's quietly wrong.",
    },
    ctaTitle: "Ready to put your FP&A work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and finance teams that need real planning reps, not spreadsheet templates.",
    opportunity: {
      ...baseOpportunity,
      id: "fpa-analysts",
      token: "fpa-analysts",
      title: "FP&A",
      payRateMin: "65",
      payRateMax: "150",
      requirements: [
        "FP&A analyst, finance manager, or corporate planner (US, 4+ yrs)",
        "Budgeting, forecasting, or operating-model experience",
      ],
      referralTitle: "FP&A",
      spotsRemaining: 58,
    },
  },
  {
    slug: "treasury-analysts",
    parentToken: "treasury-analysts",
    metaTitle: "Treasury Analysts: Get Paid $60-$150/hr to Evaluate AI",
    metaDescription:
      "Treasury analysts and cash managers earn $60-$150/hr reviewing AI liquidity, hedging, and cash-flow output. Remote, hourly, on your schedule.",
    heroEyebrow: "Treasury Network",
    heroLabel: "treasury and cash management work",
    heroTitle: "Your treasury judgment, now worth $60-$150 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the liquidity forecasts, hedges, and covenants you already manage. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a treasury analyst's eye on a liquidity forecast",
    detail: {
      summary:
        "Review a model's liquidity forecasts and hedging strategies the way you'd review them before a funding decision. Flag the covenant risk and the hedge that doesn't offset.",
    },
    ctaTitle: "Ready to put your treasury work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and finance teams that need real treasury reps, not textbook corporate finance.",
    opportunity: {
      ...baseOpportunity,
      id: "treasury-analysts",
      token: "treasury-analysts",
      title: "Treasury",
      payRateMin: "60",
      payRateMax: "150",
      requirements: [
        "Treasury analyst, cash manager, or corporate finance pro (US, 4+ yrs)",
        "Liquidity, hedging, or capital-markets experience",
      ],
      referralTitle: "Treasury",
      spotsRemaining: 41,
    },
  },
  {
    slug: "credit-risk-analysts",
    parentToken: "credit-risk-analysts",
    metaTitle: "Credit & Risk Analysts: $65-$160/hr to Evaluate AI",
    metaDescription:
      "Credit analysts and risk pros earn $65-$160/hr reviewing AI underwriting, credit memos, and risk models. Remote, hourly, on your schedule.",
    heroEyebrow: "Credit Risk Network",
    heroLabel: "credit and risk analysis work",
    heroTitle: "Your credit judgment, now worth $65-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the underwriting, limits, and credit memos you already decide. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a credit analyst's eye on a memo that buries the risk",
    detail: {
      summary:
        "Review a model's credit memos and underwriting the way you'd review a deal before committee. Flag the optimistic projection, the covenant that won't hold, the risk the spreadsheet hides.",
    },
    ctaTitle: "Ready to put your credit work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and lenders that need real underwriting judgment, not credit-scoring theory.",
    opportunity: {
      ...baseOpportunity,
      id: "credit-risk-analysts",
      token: "credit-risk-analysts",
      title: "Credit & Risk",
      payRateMin: "65",
      payRateMax: "160",
      requirements: [
        "Credit analyst, underwriter, or risk manager (US, 4+ yrs)",
        "Commercial, consumer, or counterparty credit experience",
      ],
      referralTitle: "Credit & Risk",
      spotsRemaining: 47,
    },
  },
  {
    slug: "corporate-development",
    parentToken: "corporate-development",
    metaTitle: "Corporate Development & M&A: $80-$180/hr to Evaluate AI",
    metaDescription:
      "Corp dev and M&A pros earn $80-$180/hr reviewing AI deal models, diligence, and valuation. Remote, hourly, on your schedule.",
    heroEyebrow: "Corp Dev Network",
    heroLabel: "corporate development and M&A work",
    heroTitle: "Your M&A judgment, now worth $80-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the deal models, diligence, and valuations you already run. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a corp dev lead's eye on a deal model that won't close",
    detail: {
      summary:
        "Review a model's deal models and valuations the way you'd review them before an investment committee. Flag the synergy that's fiction, the multiple that's wrong, the risk diligence missed.",
    },
    ctaTitle: "Ready to put your deal work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and deal teams that need real M&A reps, not valuation textbooks.",
    opportunity: {
      ...baseOpportunity,
      id: "corporate-development",
      token: "corporate-development",
      title: "Corporate Development",
      payRateMin: "80",
      payRateMax: "180",
      requirements: [
        "Corp dev, M&A, or investment banking professional (US, 3+ yrs)",
        "Deal modeling, diligence, or valuation experience",
      ],
      referralTitle: "Corporate Development",
      spotsRemaining: 34,
    },
  },
  {
    slug: "tax-accountants",
    parentToken: "tax-accountants",
    metaTitle: "Tax Experts: $55-$145/hr, Remote, Paid Hourly",
    metaDescription:
      "CPAs, EAs, and tax advisors earn $55-$145/hr reviewing AI tax work remotely. A few hours a week, paid hourly, on your schedule.",
    heroEyebrow: "Tax Network",
    heroLabel: "tax and advisory work",
    heroTitle: "Your tax judgment, now worth $55-$145 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the positions you'd defend and the deductions you'd never take. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a tax CPA's eye on an aggressive deduction",
    detail: {
      summary:
        "You review AI tax positions, returns, and planning the way you'd review work before signing it, flagging the position that won't survive an audit. A few hours a week, remote, paid hourly.",
    },
    ctaTitle: "Ready to put your tax work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote projects from AI labs and fintechs that need real tax judgment. Paid hourly, on your schedule.",
    opportunity: {
      ...baseOpportunity,
      id: "tax-accountants",
      token: "tax-accountants",
      title: "Tax",
      payRateMin: "55",
      payRateMax: "145",
      requirements: [
        "CPA, EA, or tax accountant (US, 4+ yrs)",
        "Individual, corporate, partnership, or international tax experience",
      ],
      referralTitle: "Tax",
      spotsRemaining: 53,
    },
  },
  {
    slug: "auditors",
    parentToken: "auditors",
    metaTitle: "Auditor Experts: $55-$140/hr, Remote, Paid Hourly",
    metaDescription:
      "External and internal auditors earn $55-$140/hr reviewing AI audit work remotely. A few hours a week, paid hourly, on your schedule.",
    heroEyebrow: "Audit Network",
    heroLabel: "audit and assurance work",
    heroTitle: "Your audit judgment, now worth $55-$140 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the controls you test and the findings you write. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "an auditor's eye on a control that doesn't actually work",
    detail: {
      summary:
        "You review AI audit procedures, testing, and findings the way you'd review a staff auditor's workpapers, flagging the control that's a paper tiger. A few hours a week, remote, paid hourly.",
    },
    ctaTitle: "Ready to put your audit work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote projects from AI labs and fintechs that need real audit reps. Paid hourly, on your schedule.",
    opportunity: {
      ...baseOpportunity,
      id: "auditors",
      token: "auditors",
      title: "Audit",
      payRateMin: "55",
      payRateMax: "140",
      requirements: [
        "External, internal, or IT auditor (US, 4+ yrs)",
        "Financial statement, SOX, or operational audit experience",
      ],
      referralTitle: "Audit",
      spotsRemaining: 50,
    },
  },
  {
    slug: "controllers",
    parentToken: "controllers",
    metaTitle: "Controller Experts: $70-$160/hr, Remote, Paid Hourly",
    metaDescription:
      "Controllers and accounting managers earn $70-$160/hr reviewing AI close and reporting work remotely. A few hours a week, paid hourly, on your schedule.",
    heroEyebrow: "Controller Network",
    heroLabel: "accounting and controllership work",
    heroTitle: "Your controller judgment, now worth $70-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the closes you own and the policies you set. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a controller's eye on a close that won't reconcile",
    detail: {
      summary:
        "You review AI close workflows, reporting, and accounting treatments the way you'd review them before they hit the financials, flagging the misclassification. A few hours a week, remote, paid hourly.",
    },
    ctaTitle: "Ready to put your accounting work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote projects from AI labs and fintechs that need real controllership reps. Paid hourly, on your schedule.",
    opportunity: {
      ...baseOpportunity,
      id: "controllers",
      token: "controllers",
      title: "Controllership",
      payRateMin: "70",
      payRateMax: "160",
      requirements: [
        "Controller, assistant controller, or accounting manager (US, 5+ yrs)",
        "Close ownership, technical accounting, or financial reporting experience",
      ],
      referralTitle: "Controllership",
      spotsRemaining: 38,
    },
  },
  {
    slug: "forensic-accountants",
    parentToken: "forensic-accountants",
    metaTitle: "Forensic Accountants: $70-$170/hr, Remote, Paid Hourly",
    metaDescription:
      "Forensic accountants and fraud examiners earn $70-$170/hr reviewing AI fraud and investigation work remotely. A few hours a week, paid hourly, on your schedule.",
    heroEyebrow: "Forensic Network",
    heroLabel: "forensic accounting and investigation work",
    heroTitle: "Your forensic judgment, now worth $70-$170 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the schemes you've unwound and the trails others miss. Paid hourly, on your schedule.",
    badgePersonaId: "product",
    letterExample:
      "a forensic accountant's eye on a transaction that doesn't add up",
    detail: {
      summary:
        "You review AI fraud analysis and investigative findings the way you'd review an examiner's report, flagging the explained-away anomaly and the missing audit trail. A few hours a week, remote, paid hourly.",
    },
    ctaTitle: "Ready to put your forensic work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote projects from AI labs that need real investigative judgment. Paid hourly, on your schedule.",
    opportunity: {
      ...baseOpportunity,
      id: "forensic-accountants",
      token: "forensic-accountants",
      title: "Forensic Accounting",
      payRateMin: "70",
      payRateMax: "170",
      requirements: [
        "Forensic accountant, CFE, or fraud examiner (US, 4+ yrs)",
        "Investigations, litigation support, or AML/financial-crime experience",
      ],
      referralTitle: "Forensic Accounting",
      spotsRemaining: 29,
    },
  },
  {
    slug: "medical-coders",
    parentToken: "medical-coders",
    metaTitle: "Medical Coder Experts: $45-$110/hr Reviewing AI",
    metaDescription:
      "Certified coders (CPC, CCS) earn $45-$110/hr, remote, evaluating a model's codes, modifiers, and claim accuracy a few hours a week. Paid hourly per task.",
    heroEyebrow: "Medical Coding Network",
    heroLabel: "medical coding work",
    heroTitle: "Your coding accuracy, $45-$110 an hour.",
    heroSubtitle:
      "CPC, CCS, and specialty-certified coders, catch the upcode, the unbundling, the unsupported claim in a model's output. Remote, a few hours a week, paid per task.",
    badgePersonaId: "doctor",
    letterExample: "a CPC coder's eye on an unbundled claim",
    detail: {
      summary:
        "Review a model's codes, modifiers, and claims like a queue before submission, flagging the upcode, the unbundling, the documentation that won't support it. Remote, paid hourly, on your schedule.",
    },
    ctaTitle: "Ready to put your coding work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and healthtech that need real coding reps, not a code book.",
    opportunity: {
      ...baseOpportunity,
      id: "medical-coders",
      token: "medical-coders",
      title: "Medical Coding",
      payRateMin: "45",
      payRateMax: "110",
      requirements: [
        "CPC, CCS, CRC, or specialty-certified coder (US, 3+ yrs)",
        "Inpatient, outpatient, or risk-adjustment coding experience",
      ],
      referralTitle: "Medical Coding",
      spotsRemaining: 76,
    },
  },
  {
    slug: "clinical-documentation",
    parentToken: "clinical-documentation",
    metaTitle: "CDI Specialist Experts: $55-$125/hr Reviewing AI",
    metaDescription:
      "CDI specialists earn $55-$125/hr, remote, evaluating AI-drafted documentation, queries, and DRG assignment a few hours a week. Paid hourly per task.",
    heroEyebrow: "CDI Network",
    heroLabel: "clinical documentation work",
    heroTitle: "Your documentation judgment, $55-$125 an hour.",
    heroSubtitle:
      "CDI specialists, documentation nurses, and DRG validators, catch the unsupported diagnosis and the leading query in a model's output. Remote, a few hours a week, paid per task.",
    badgePersonaId: "doctor",
    letterExample:
      "a CDI specialist's eye on a record that won't support the DRG",
    detail: {
      summary:
        "Review AI-drafted documentation, provider queries, and DRG assignments like a chart before it drops, flagging the unsupported diagnosis, the missed comorbidity, the leading query. Remote, paid hourly, on your schedule.",
    },
    ctaTitle: "Ready to put your CDI work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and healthtech that need real documentation reps, not coding guidelines.",
    opportunity: {
      ...baseOpportunity,
      id: "clinical-documentation",
      token: "clinical-documentation",
      title: "Clinical Documentation",
      payRateMin: "55",
      payRateMax: "125",
      requirements: [
        "CDI specialist, CCDS, or documentation RN (US, 3+ yrs)",
        "Inpatient CDI, DRG validation, or query experience",
      ],
      referralTitle: "Clinical Documentation",
      spotsRemaining: 54,
    },
  },
  {
    slug: "revenue-cycle",
    parentToken: "revenue-cycle",
    metaTitle: "Revenue Cycle Experts: $50-$115/hr Reviewing AI",
    metaDescription:
      "Revenue cycle, billing, and denials pros earn $50-$115/hr, remote, evaluating AI-drafted claims, appeals, and reimbursement a few hours a week. Paid hourly.",
    heroEyebrow: "Revenue Cycle Network",
    heroLabel: "revenue cycle and billing work",
    heroTitle: "Your billing and appeals judgment, $50-$115 an hour.",
    heroSubtitle:
      "Revenue cycle analysts, billers, and denials specialists, catch the claim that bounces and the appeal that won't stick in a model's output. Remote, a few hours a week, paid hourly.",
    badgePersonaId: "doctor",
    letterExample:
      "a revenue cycle analyst's eye on a denial that should be appealed",
    detail: {
      summary:
        "Review AI-drafted claims, denials, and appeals like a worklist before close, flagging the claim that bounces, the appeal that won't stick, the write-off worth fighting. Remote, paid hourly, on your schedule.",
    },
    ctaTitle: "Ready to put your revenue cycle work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and healthtech that need real reimbursement reps, not payer-policy PDFs.",
    opportunity: {
      ...baseOpportunity,
      id: "revenue-cycle",
      token: "revenue-cycle",
      title: "Revenue Cycle",
      payRateMin: "50",
      payRateMax: "115",
      requirements: [
        "Revenue cycle, billing, or denials specialist (US, 3+ yrs)",
        "Claims, appeals, AR, or payer-contract experience",
      ],
      referralTitle: "Revenue Cycle",
      spotsRemaining: 63,
    },
  },
  {
    slug: "utilization-management",
    parentToken: "utilization-management",
    metaTitle: "Utilization Management Nurses: $60-$130/hr Reviewing AI",
    metaDescription:
      "UM nurses and medical reviewers earn $60-$130/hr, remote, evaluating AI-drafted authorizations, medical necessity, and appeals a few hours a week. Paid hourly.",
    heroEyebrow: "Utilization Management Network",
    heroLabel: "utilization management work",
    heroTitle: "Your medical necessity judgment, $60-$130 an hour.",
    heroSubtitle:
      "UM nurses, medical reviewers, and prior-auth specialists, catch the wrongful denial and the misapplied criteria in a model's output. Remote, a few hours a week, paid hourly.",
    badgePersonaId: "doctor",
    letterExample: "a UM nurse's eye on a prior auth that shouldn't be denied",
    detail: {
      summary:
        "Review AI-drafted authorizations, necessity determinations, and appeals against criteria, flagging the wrongful denial, the misapplied InterQual or MCG rule, the safety issue a flowchart misses. Remote, paid hourly, on your schedule.",
    },
    ctaTitle: "Ready to put your UM work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and healthtech that need real utilization review reps, not policy criteria alone.",
    opportunity: {
      ...baseOpportunity,
      id: "utilization-management",
      token: "utilization-management",
      title: "Utilization Management",
      payRateMin: "60",
      payRateMax: "130",
      requirements: [
        "UM nurse, medical reviewer, or prior-auth RN (US, active license, 3+ yrs)",
        "InterQual, MCG, or medical-necessity review experience",
      ],
      referralTitle: "Utilization Management",
      spotsRemaining: 48,
    },
  },
  {
    slug: "backend-engineers",
    parentToken: "backend-engineers",
    metaTitle: "Backend Engineers: Get Paid $70-$200/hr to Review AI Code",
    metaDescription:
      "Backend and distributed systems engineers get paid $70-$200/hr to catch the race conditions and N+1 queries in AI services and APIs. Remote, hourly, your schedule.",
    heroEyebrow: "Backend Network",
    heroLabel: "backend engineering work",
    heroTitle: "Your backend judgment, now worth $70-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI services, APIs, and schemas the way you review a teammate's PR. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "a backend engineer's eye on a race condition under load",
    detail: {
      summary:
        "Review AI services and APIs for the race condition, the N+1, the design that won't survive scale. The judgment that keeps systems up under real traffic. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your engineering work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and infra teams that need real backend experience, not leetcode.",
    opportunity: {
      ...baseOpportunity,
      id: "backend-engineers",
      token: "backend-engineers",
      title: "Backend Engineering",
      payRateMin: "70",
      payRateMax: "200",
      requirements: [
        "Backend or distributed systems engineer (US/UK/CA, 3+ yrs)",
        "APIs, databases, or large-scale services experience",
      ],
      referralTitle: "Backend Engineering",
      spotsRemaining: 118,
    },
  },
  {
    slug: "frontend-engineers",
    parentToken: "frontend-engineers",
    metaTitle: "Frontend Engineers: Get Paid $65-$190/hr to Review AI Code",
    metaDescription:
      "Frontend and web engineers get paid $65-$190/hr to catch the accessibility violations and re-render bugs in AI UI code. Remote, hourly, your schedule.",
    heroEyebrow: "Frontend Network",
    heroLabel: "frontend engineering work",
    heroTitle: "Your frontend judgment, now worth $65-$190 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI UI code the way you review a PR before merge. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample:
      "a frontend engineer's eye on a component that breaks accessibility",
    detail: {
      summary:
        "Review AI UI code for the accessibility violation, the re-render storm, the state bug that only shows in production. The judgment that ships interfaces that feel right. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your engineering work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and product teams that need real frontend experience, not framework tutorials.",
    opportunity: {
      ...baseOpportunity,
      id: "frontend-engineers",
      token: "frontend-engineers",
      title: "Frontend Engineering",
      payRateMin: "65",
      payRateMax: "190",
      requirements: [
        "Frontend or web engineer (US/UK/CA, 3+ yrs)",
        "React, TypeScript, accessibility, or performance experience",
      ],
      referralTitle: "Frontend Engineering",
      spotsRemaining: 102,
    },
  },
  {
    slug: "ml-engineers",
    parentToken: "ml-engineers",
    metaTitle:
      "Machine Learning Engineers: Get Paid $80-$220/hr to Review AI Code",
    metaDescription:
      "ML and MLOps engineers get paid $80-$220/hr to catch the label leaks and overstated evals in AI training code and pipelines. Remote, hourly, your schedule.",
    heroEyebrow: "ML Engineering Network",
    heroLabel: "machine learning engineering work",
    heroTitle: "Your ML judgment, now worth $80-$220 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI training code and pipelines the way you review a model before it ships. Paid hourly, on your schedule.",
    badgePersonaId: "scientist",
    letterExample:
      "an ML engineer's eye on a training pipeline that leaks labels",
    detail: {
      summary:
        "Review AI training code and pipelines for the label leak, the eval that overstates, the serving path that silently degrades. The judgment from running real models in production. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your ML work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and ML platforms that need real production ML experience, not tutorials.",
    opportunity: {
      ...baseOpportunity,
      id: "ml-engineers",
      token: "ml-engineers",
      title: "ML Engineering",
      payRateMin: "80",
      payRateMax: "220",
      requirements: [
        "ML engineer, MLOps, or applied scientist (US/UK/CA, 3+ yrs)",
        "Shipped models to production at scale",
      ],
      referralTitle: "ML Engineering",
      spotsRemaining: 71,
    },
  },
  {
    slug: "devops-sre",
    parentToken: "devops-sre",
    metaTitle: "DevOps & SRE Experts: Get Paid $70-$200/hr to Review AI Infra",
    metaDescription:
      "DevOps, SRE, and platform engineers get paid $70-$200/hr to catch the misconfigs in AI-written Terraform, Helm, and pipelines before they cause an outage. Remote, hourly, your schedule.",
    heroEyebrow: "Platform Network",
    heroLabel: "DevOps and reliability work",
    heroTitle: "Your reliability judgment, now worth $70-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI infra the way you review a change before it touches prod. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample: "an SRE's eye on a deploy that will page someone at 3am",
    detail: {
      summary:
        "Review AI infra for the config that takes down the cluster, the rollback that won't work, the alert that's pure noise. The judgment from running real systems under real load. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your platform work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and infra teams that need real reliability experience, not certification dumps.",
    opportunity: {
      ...baseOpportunity,
      id: "devops-sre",
      token: "devops-sre",
      title: "DevOps & SRE",
      payRateMin: "70",
      payRateMax: "200",
      requirements: [
        "DevOps, SRE, or platform engineer (US/UK/CA, 3+ yrs)",
        "Kubernetes, IaC, CI/CD, or incident response experience",
      ],
      referralTitle: "DevOps & SRE",
      spotsRemaining: 84,
    },
  },
  {
    slug: "data-engineers",
    parentToken: "data-engineers",
    metaTitle: "Data Engineers: Get Paid $70-$190/hr to Review AI Pipelines",
    metaDescription:
      "Data and analytics engineers get paid $70-$190/hr to catch the silent data loss and broken idempotency in AI pipelines and warehouse models. Remote, hourly, your schedule.",
    heroEyebrow: "Data Engineering Network",
    heroLabel: "data engineering work",
    heroTitle: "Your pipeline judgment, now worth $70-$190 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI pipelines the way you review a PR before it hits the lake. Paid hourly, on your schedule.",
    badgePersonaId: "engineer",
    letterExample:
      "a data engineer's eye on a pipeline that silently drops rows",
    detail: {
      summary:
        "Review AI pipelines and warehouse models for the silent data loss, the broken idempotency, the model that fans out wrong. The judgment that keeps data trustworthy at scale. Paid hourly, remote, a few hours a week.",
    },
    ctaTitle: "Ready to put your data engineering work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and data platforms that need real pipeline experience, not SQL puzzles.",
    opportunity: {
      ...baseOpportunity,
      id: "data-engineers",
      token: "data-engineers",
      title: "Data Engineering",
      payRateMin: "70",
      payRateMax: "190",
      requirements: [
        "Data or analytics engineer (US/UK/CA, 3+ yrs)",
        "Spark, dbt, streaming, or warehouse modeling experience",
      ],
      referralTitle: "Data Engineering",
      spotsRemaining: 79,
    },
  },
  {
    slug: "strategy-consultants",
    parentToken: "strategy-consultants",
    metaTitle: "Strategy Consultants: Get Paid $90-$200/hr to Review AI",
    metaDescription:
      "Strategy consultants earn $90-$200/hr, remote, pressure-testing AI on market analysis, growth strategy, and partner-level logic. A few hours a week, paid per task.",
    heroEyebrow: "Strategy Network",
    heroLabel: "strategy consulting work",
    heroTitle: "Your strategy judgment, now worth $90-$200 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the market sizing, growth bets, and logic gaps you already catch. Paid per task, on your schedule.",
    badgePersonaId: "consultant",
    letterExample:
      "a strategy consultant's eye on a market sizing that doesn't hold up",
    detail: {
      summary:
        "Review a model's market analysis the way a partner reviews an associate's deck. Flag the sizing built on a bad assumption, the strategy with no path to execution, the gap a client would catch.",
    },
    ctaTitle: "Ready to put your strategy work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and strategy teams that need real reps, not framework slides.",
    opportunity: {
      ...baseOpportunity,
      id: "strategy-consultants",
      token: "strategy-consultants",
      title: "Strategy Consulting",
      payRateMin: "90",
      payRateMax: "200",
      requirements: [
        "Strategy consultant or corporate strategy lead (4+ yrs)",
        "Market entry, growth, or corporate strategy experience",
      ],
      referralTitle: "Strategy Consulting",
      spotsRemaining: 62,
    },
  },
  {
    slug: "operations-consultants",
    parentToken: "operations-consultants",
    metaTitle: "Operations Consultants: Get Paid $80-$180/hr to Review AI",
    metaDescription:
      "Operations and implementation consultants earn $80-$180/hr, remote, reviewing AI on process design, transformation, and operating models. A few hours a week, paid per task.",
    heroEyebrow: "Operations Network",
    heroLabel: "operations consulting work",
    heroTitle: "Your operations judgment, now worth $80-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing AI on the processes, operating models, and transformations you already land. Paid per task, on your schedule.",
    badgePersonaId: "consultant",
    letterExample:
      "an ops consultant's eye on a transformation that won't stick",
    detail: {
      summary:
        "Pressure-test a model's process designs and transformation plans the way you would with a client. Flag the savings that will not materialize, the process that ignores the front line, the change no one adopts.",
    },
    ctaTitle: "Ready to put your operations work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and operations teams that need real implementation reps, not maturity models.",
    opportunity: {
      ...baseOpportunity,
      id: "operations-consultants",
      token: "operations-consultants",
      title: "Operations Consulting",
      payRateMin: "80",
      payRateMax: "180",
      requirements: [
        "Operations or implementation consultant (4+ yrs)",
        "Process redesign, cost transformation, or PMO experience",
      ],
      referralTitle: "Operations Consulting",
      spotsRemaining: 55,
    },
  },
  {
    slug: "ex-mbb-consultants",
    parentToken: "ex-mbb-consultants",
    metaTitle: "Ex-MBB Consultants: Get Paid $100-$220/hr to Review AI",
    metaDescription:
      "Former McKinsey, Bain, and BCG consultants earn $100-$220/hr, remote, running case interviews and reviewing AI strategy at partner-level logic. Paid per task.",
    heroEyebrow: "Ex-MBB Network",
    heroLabel: "top-tier strategy consulting work",
    heroTitle: "Your MBB judgment, now worth $100-$220 an hour.",
    heroSubtitle:
      "A few remote hours a week running AI through cases and stress-testing the strategy you would defend to a partner. Paid per task, on your schedule.",
    badgePersonaId: "consultant",
    letterExample:
      "an ex-McKinsey EM's eye on a case that breaks at the second layer",
    detail: {
      summary:
        "Run models through case interviews and stress-test their strategy the way a partner would. Flag the MECE that isn't, the hypothesis with no support, the recommendation that collapses under one hard question.",
    },
    ctaTitle: "Ready to put your consulting work on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and strategy teams that need real top-tier reps, not casebook frameworks.",
    opportunity: {
      ...baseOpportunity,
      id: "ex-mbb-consultants",
      token: "ex-mbb-consultants",
      title: "Ex-MBB Consulting",
      payRateMin: "100",
      payRateMax: "220",
      requirements: [
        "Former McKinsey, Bain, or BCG consultant (2+ yrs at firm)",
        "Engagement-level case and client experience",
      ],
      referralTitle: "Ex-MBB Consulting",
      spotsRemaining: 44,
    },
  },
  {
    slug: "product-managers",
    parentToken: "product-managers",
    metaTitle: "Product Manager Experts: $70-$160/hr Evaluating AI",
    metaDescription:
      "Senior product managers earn $70-$160/hr reviewing AI-drafted PRDs, prioritization, and strategy. Remote, a few hours a week, paid per task.",
    heroEyebrow: "Product Network",
    heroLabel: "product management work",
    heroTitle: "Your roadmap judgment, now worth $70-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the specs you write and the tradeoffs you defend. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a senior PM's eye on a PRD that solves the wrong problem",
    detail: {
      summary:
        "Review AI-drafted PRDs, prioritization, and strategy the way you'd review a PM on your team. Flag the feature chasing a non-problem and the metric that will be gamed. Remote and paid hourly.",
    },
    ctaTitle: "Put your product work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and product teams that need real PM reps, not frameworks from a blog.",
    opportunity: {
      ...baseOpportunity,
      id: "product-managers",
      token: "product-managers",
      title: "Product Management",
      payRateMin: "70",
      payRateMax: "160",
      requirements: [
        "Senior PM, group PM, or product lead (US/UK/CA, 4+ yrs)",
        "Shipped consumer or B2B products end to end",
      ],
      referralTitle: "Product Management",
      spotsRemaining: 96,
    },
  },
  {
    slug: "technical-program-managers",
    parentToken: "technical-program-managers",
    metaTitle: "Technical Program Managers: $80-$180/hr Evaluating AI",
    metaDescription:
      "Technical program managers earn $80-$180/hr reviewing AI-drafted planning, cross-team execution, and technical tradeoffs. Remote, paid per task.",
    heroEyebrow: "TPM Network",
    heroLabel: "technical program management work",
    heroTitle: "Your program execution, now worth $80-$180 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the dependencies you untangle and the launches you land across teams. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample: "a TPM's eye on a plan that hides a critical dependency",
    detail: {
      summary:
        "Review AI-drafted program plans, risk registers, and cross-team coordination the way you'd review a launch plan before commit. Flag the dependency no one owns. Remote and paid hourly.",
    },
    ctaTitle: "Put your program work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and engineering teams that need real TPM reps, not Gantt charts.",
    opportunity: {
      ...baseOpportunity,
      id: "technical-program-managers",
      token: "technical-program-managers",
      title: "Technical Program Management",
      payRateMin: "80",
      payRateMax: "180",
      requirements: [
        "TPM or engineering program lead (US/UK/CA, 4+ yrs)",
        "Cross-team execution on technical programs",
      ],
      referralTitle: "Technical Program Management",
      spotsRemaining: 58,
    },
  },
  {
    slug: "growth-product-managers",
    parentToken: "growth-product-managers",
    metaTitle: "Growth Product Managers: $70-$160/hr Evaluating AI",
    metaDescription:
      "Growth product managers earn $70-$160/hr reviewing AI-drafted experiments, funnels, and growth strategy. Remote, a few hours a week, paid per task.",
    heroEyebrow: "Growth Network",
    heroLabel: "growth product work",
    heroTitle: "Your experiment judgment, now worth $70-$160 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the funnels you fix and the activation you've actually moved. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample:
      "a growth PM's eye on an experiment that won't be conclusive",
    detail: {
      summary:
        "Review AI-drafted experiments, funnel analyses, and growth strategy before a launch decision. Flag the underpowered test and the funnel fix that just shifts the drop-off. Remote and paid hourly.",
    },
    ctaTitle: "Put your growth work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and growth teams that need real experimentation reps, not listicles.",
    opportunity: {
      ...baseOpportunity,
      id: "growth-product-managers",
      token: "growth-product-managers",
      title: "Growth Product",
      payRateMin: "70",
      payRateMax: "160",
      requirements: [
        "Growth PM, lifecycle, or monetization lead (US/UK/CA, 3+ yrs)",
        "Owned activation, retention, or monetization metrics",
      ],
      referralTitle: "Growth Product",
      spotsRemaining: 64,
    },
  },
  {
    slug: "ai-product-managers",
    parentToken: "ai-product-managers",
    metaTitle: "AI Product Managers: $90-$190/hr Evaluating AI",
    metaDescription:
      "AI and ML product managers earn $90-$190/hr reviewing AI-drafted specs, model tradeoffs, and AI-feature strategy. Remote, a few hours a week, paid per task.",
    heroEyebrow: "AI Product Network",
    heroLabel: "AI product management work",
    heroTitle: "Your AI product judgment, now worth $90-$190 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing AI on the evals you define and the failure modes you design around. Paid per task, on your schedule.",
    badgePersonaId: "product",
    letterExample:
      "an AI PM's eye on a feature that ignores how the model fails",
    detail: {
      summary:
        "Review AI-drafted specs, model tradeoffs, and AI-feature strategy the way you'd review a launch built on a model you don't fully control. Flag the spec that ignores hallucination. Remote and paid hourly.",
    },
    ctaTitle: "Put your AI product work to use.",
    ctaSubtitle:
      "Apply once. Get matched to projects from AI labs and product teams that need real AI product reps, not prompt threads.",
    opportunity: {
      ...baseOpportunity,
      id: "ai-product-managers",
      token: "ai-product-managers",
      title: "AI Product Management",
      payRateMin: "90",
      payRateMax: "190",
      requirements: [
        "AI/ML product manager (US/UK/CA, 3+ yrs)",
        "Shipped model-powered or ML features to production",
      ],
      referralTitle: "AI Product Management",
      spotsRemaining: 52,
    },
  },
  {
    slug: "primary-care",
    parentToken: "primary-care",
    metaTitle: "Primary Care Experts: Earn $90-$280/hr Reviewing AI",
    metaDescription:
      "Primary care providers earn $90-$280/hr, fully remote, reviewing AI-drafted diagnoses, preventive plans, and patient guidance against real ambulatory judgment. A few hours a week, paid per task.",
    heroEyebrow: "Primary Care Network",
    heroLabel: "ambulatory and preventive care work",
    heroTitle: "Your primary care judgment, now worth $90-$280 an hour.",
    heroSubtitle:
      "A few remote hours a week checking medical AI - the fatigue you work up, the screening you press, the referral you hold. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a generalist's read on a chief complaint that hides something bigger",
    detail: {
      summary:
        "Review AI-drafted visit notes, preventive plans, and triage advice the way you'd review your own panel: flag the missed red flag, defend why you'd watch and wait. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your ambulatory judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real primary care reasoning, not guideline recitation.",
    opportunity: {
      ...baseOpportunity,
      id: "primary-care",
      token: "primary-care",
      title: "Primary Care",
      payRateMin: "90",
      payRateMax: "280",
      requirements: [
        "Licensed primary care provider (MD, DO, NP, or PA) in active or recent outpatient practice",
        "Experience managing an undifferentiated, longitudinal adult patient panel",
      ],
      referralTitle: "Primary Care",
      spotsRemaining: 52,
    },
  },
  {
    slug: "internal-medicine",
    parentToken: "internal-medicine",
    metaTitle: "Internal Medicine Experts: Earn $100-$290/hr Reviewing AI",
    metaDescription:
      "Internists earn $100-$290/hr, fully remote, reviewing AI-drafted workups, multi-system reasoning, and management plans against real internal medicine judgment. A few hours a week, paid per task.",
    heroEyebrow: "Internal Medicine Network",
    heroLabel: "complex adult medicine work",
    heroTitle: "Your internal medicine reasoning, now worth $100-$290 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the comorbidities you balance, the polypharmacy you untangle, the workup you sequence. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an internist's read on a workup that ordered the wrong test first",
    detail: {
      summary:
        "Review AI-drafted differentials, problem lists, and management plans the way you'd reason through morning report: flag the anchoring error, treat the patient not the number. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your internal medicine reasoning on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real multi-system judgment, not memorized algorithms.",
    opportunity: {
      ...baseOpportunity,
      id: "internal-medicine",
      token: "internal-medicine",
      title: "Internal Medicine",
      payRateMin: "100",
      payRateMax: "290",
      requirements: [
        "Board-certified internist (ABIM or equivalent), US active or recent practice",
        "Experience managing complex, multi-comorbidity adult patients across care settings",
      ],
      referralTitle: "Internal Medicine",
      spotsRemaining: 44,
    },
  },
  {
    slug: "family-medicine",
    parentToken: "family-medicine",
    metaTitle: "Family Medicine Experts: Earn $90-$280/hr Reviewing AI",
    metaDescription:
      "Family physicians earn $90-$280/hr, fully remote, reviewing AI-drafted care plans across pediatric, adult, and geriatric medicine against real full-spectrum judgment. A few hours a week, paid per task.",
    heroEyebrow: "Family Medicine Network",
    heroLabel: "full-spectrum family care work",
    heroTitle: "Your full-spectrum judgment, now worth $90-$280 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week checking medical AI - the well-child vaccine talk, the geriatric polypharmacy, the same symptom across ages. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a family doc's read on advice that fit the textbook but not the family",
    detail: {
      summary:
        "Review AI-drafted care plans across pediatrics, adult, and geriatrics the way you'd manage a family on your panel: flag the advice that ignores the caregiver, defend watchful waiting. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your full-spectrum judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real continuity-of-care reasoning, not single-encounter guidelines.",
    opportunity: {
      ...baseOpportunity,
      id: "family-medicine",
      token: "family-medicine",
      title: "Family Medicine",
      payRateMin: "90",
      payRateMax: "280",
      requirements: [
        "Board-certified family physician (ABFM or equivalent), US active or recent practice",
        "Full-spectrum experience spanning pediatric, adult, and geriatric care",
      ],
      referralTitle: "Family Medicine",
      spotsRemaining: 61,
    },
  },
  {
    slug: "hospital-medicine",
    parentToken: "hospital-medicine",
    metaTitle: "Hospital Medicine Experts: Earn $110-$300/hr Reviewing AI",
    metaDescription:
      "Hospitalists earn $110-$300/hr, fully remote, reviewing AI-drafted admission notes, inpatient management, and discharge plans against real acute-care judgment. A few hours a week, paid per task.",
    heroEyebrow: "Hospital Medicine Network",
    heroLabel: "acute inpatient care work",
    heroTitle: "Your inpatient judgment, now worth $110-$300 an hour.",
    heroSubtitle:
      "A few remote hours a week pressure-testing medical AI - the rapid response you run, the trend you catch, the discharge you defend. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a hospitalist's read on a discharge that was set up to bounce back",
    detail: {
      summary:
        "Review AI-drafted admission H&Ps, progress notes, and discharge summaries the way you'd run your service: flag the premature de-escalation, defend why a patient isn't ready to leave. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your inpatient judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real acute-care reasoning, not stable-patient protocols.",
    opportunity: {
      ...baseOpportunity,
      id: "hospital-medicine",
      token: "hospital-medicine",
      title: "Hospital Medicine",
      payRateMin: "110",
      payRateMax: "300",
      requirements: [
        "Board-certified physician practicing hospital medicine (IM or FM base), US active or recent",
        "Experience managing acute inpatient admissions, rapid responses, and discharge planning",
      ],
      referralTitle: "Hospital Medicine",
      spotsRemaining: 37,
    },
  },
  {
    slug: "pediatrics",
    parentToken: "pediatrics",
    metaTitle: "Pediatrics Experts: Earn $100-$290/hr Reviewing AI",
    metaDescription:
      "Pediatricians earn $100-$290/hr, fully remote, reviewing AI-drafted diagnoses, weight-based dosing, and parent guidance against real child-health judgment. A few hours a week, paid per task.",
    heroEyebrow: "Pediatrics Network",
    heroLabel: "child and adolescent health work",
    heroTitle: "Your pediatric judgment, now worth $100-$290 an hour.",
    heroSubtitle:
      "A few remote hours a week checking medical AI - the toxic-versus-well call from the doorway, the weight-based dose, the worried-parent talk. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a pediatrician's read on a dose that was right for an adult, wrong for a child",
    detail: {
      summary:
        "Review AI-drafted pediatric notes, dosing, and parent guidance the way you'd review a colleague's clinic: flag the adult-extrapolated dose, defend watch-and-recheck over reflexive antibiotics. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Ready to put your pediatric judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real child-health reasoning, not adult guidelines scaled down.",
    opportunity: {
      ...baseOpportunity,
      id: "pediatrics",
      token: "pediatrics",
      title: "Pediatrics",
      payRateMin: "100",
      payRateMax: "290",
      requirements: [
        "Board-certified pediatrician (ABP or equivalent), US active or recent practice",
        "Experience with weight-based dosing and age-specific developmental assessment",
      ],
      referralTitle: "Pediatrics",
      spotsRemaining: 48,
    },
  },
  {
    slug: "medical-oncology",
    parentToken: "medical-oncology",
    metaTitle: "Medical Oncology Experts: $130-$350/hr, Remote",
    metaDescription:
      "Medical oncologists earn $130-$350/hr reviewing medical AI on their own schedule. A few remote hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Medical Oncology Network",
    heroLabel: "systemic cancer therapy work",
    heroTitle: "Your oncology judgment, now worth $130-$350 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the line you'd switch, the dose you'd hold, the driver you'd treat. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an oncologist catching a contraindicated regimen for an EGFR-mutant patient",
    detail: {
      summary:
        "Read a model's treatment plans and tumor board summaries the way you'd review a fellow. Flag the regimen that ignores a driver mutation or the dose that should have been held.",
    },
    ctaTitle: "Ready to put your oncology decisions on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and healthtech teams that need real systemic-therapy judgment, not memorized NCCN tables.",
    opportunity: {
      ...baseOpportunity,
      id: "medical-oncology",
      token: "medical-oncology",
      title: "Medical Oncology",
      payRateMin: "130",
      payRateMax: "350",
      requirements: [
        "Board-certified medical oncologist (ABIM Medical Oncology, US active or recent practice)",
        "Fellowship-trained in hematology-oncology or medical oncology with solid-tumor experience",
      ],
      referralTitle: "Medical Oncology",
      spotsRemaining: 42,
    },
  },
  {
    slug: "oncology-subspecialties",
    parentToken: "oncology-subspecialties",
    metaTitle: "Oncology Subspecialty Experts: $130-$360/hr, Remote",
    metaDescription:
      "Hematology-oncology and organ-specific subspecialists earn $130-$360/hr reviewing medical AI in their own disease. A few remote hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Oncology Subspecialty Network",
    heroLabel: "disease-focused cancer care work",
    heroTitle: "Your disease-specific judgment, now worth $130-$360 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the trial you'd enroll, the maintenance you'd pick, the regimen you'd refuse. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a heme-onc specialist flagging a wrong-risk-stratification call in an AML plan",
    detail: {
      summary:
        "Evaluate a model's output in your corner of oncology - a HER2-low breast case, a transplant conditioning call, a CAR-T eligibility decision. Flag the recommendation that misreads a biomarker or picks the wrong trial.",
    },
    ctaTitle: "Ready to put your subspecialty judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and healthtech teams that need real disease-specific depth, not a generalist's summary.",
    opportunity: {
      ...baseOpportunity,
      id: "oncology-subspecialties",
      token: "oncology-subspecialties",
      title: "Oncology Subspecialties",
      payRateMin: "130",
      payRateMax: "360",
      requirements: [
        "Fellowship-trained subspecialist in hematology-oncology or an organ-specific oncology focus (breast, GI, GU, thoracic, gyn-onc, neuro-onc, heme malignancies, or BMT)",
        "Board-certified (ABIM Hematology and/or Medical Oncology) with active or recent disease-focused practice",
      ],
      referralTitle: "Oncology Subspecialties",
      spotsRemaining: 56,
    },
  },
  {
    slug: "radiation-oncology",
    parentToken: "radiation-oncology",
    metaTitle: "Radiation Oncology Experts: $140-$370/hr, Remote",
    metaDescription:
      "Radiation oncologists earn $140-$370/hr reviewing medical AI on their own schedule. A few remote hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Radiation Oncology Network",
    heroLabel: "radiotherapy planning work",
    heroTitle: "Your radiotherapy judgment, now worth $140-$370 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the margin you'd tighten, the dose you'd escalate, the cord you'd protect. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a rad-onc spotting an OAR constraint violation in an SBRT plan",
    detail: {
      summary:
        "Review a model's prescriptions and contours the way you'd check a resident before sim. Flag the fractionation that ignores the evidence, the wrong PTV margin, or the dose that blows a cord constraint.",
    },
    ctaTitle: "Ready to put your radiotherapy planning on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and healthtech teams that need real treatment-planning judgment, not a recited dose table.",
    opportunity: {
      ...baseOpportunity,
      id: "radiation-oncology",
      token: "radiation-oncology",
      title: "Radiation Oncology",
      payRateMin: "140",
      payRateMax: "370",
      requirements: [
        "Board-certified radiation oncologist (ABR Radiation Oncology, US active or recent practice)",
        "Residency-trained in radiation oncology with hands-on treatment-planning and contouring experience",
      ],
      referralTitle: "Radiation Oncology",
      spotsRemaining: 34,
    },
  },
  {
    slug: "surgical-oncology",
    parentToken: "surgical-oncology",
    metaTitle: "Surgical Oncology Experts: $150-$400/hr, Remote",
    metaDescription:
      "Surgical oncologists earn $150-$400/hr reviewing medical AI on their own schedule. A few remote hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Surgical Oncology Network",
    heroLabel: "cancer surgery work",
    heroTitle: "Your operative judgment, now worth $150-$400 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the vessel you'd spare, the nodes you'd clear, the case you'd downstage. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a surgical oncologist flagging an unresectable case called operable",
    detail: {
      summary:
        "Evaluate a model's operative plans and resectability calls the way you'd scrutinize a case at tumor board. Flag the borderline pancreas head called operable, the missed neoadjuvant indication, the inadequate margin.",
    },
    ctaTitle: "Ready to put your operative judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid projects from AI labs and healthtech teams that need real surgical-decision judgment, not a textbook indication list.",
    opportunity: {
      ...baseOpportunity,
      id: "surgical-oncology",
      token: "surgical-oncology",
      title: "Surgical Oncology",
      payRateMin: "150",
      payRateMax: "400",
      requirements: [
        "Board-certified general surgeon with Complex General Surgical Oncology fellowship training (or equivalent), US active or recent practice",
        "Hands-on cancer operative experience with resectability assessment and margin and nodal management",
      ],
      referralTitle: "Surgical Oncology",
      spotsRemaining: 29,
    },
  },
  {
    slug: "neurology",
    parentToken: "neurology",
    metaTitle: "Neurology Experts: $120-$330/hr, Remote",
    metaDescription:
      "Board-certified neurologists earn $120-$330/hr reviewing medical AI. Remote, a few hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Neurology Network",
    heroLabel: "neurologic localization and diagnosis",
    heroTitle: "Your neurology judgment, now worth $120-$330 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - localizing the lesion, reading the EEG, protecting the stroke window. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a neurologist catching a localization that does not add up",
    detail: {
      summary:
        "Read a model's neuro notes, localization arguments, and EEG reads the way you would staff a fellow's consult. Flag the brainstem sign it missed and explain why the deficit cannot come from that lesion.",
    },
    ctaTitle: "Ready to put your neurologic judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real localization, not memorized neuroanatomy.",
    opportunity: {
      ...baseOpportunity,
      id: "neurology",
      token: "neurology",
      title: "Neurology",
      payRateMin: "120",
      payRateMax: "330",
      requirements: [
        "Board-certified neurologist (US, active or recent practice)",
        "Fellowship training in stroke, epilepsy, neuromuscular, or movement disorders preferred",
      ],
      referralTitle: "Neurology",
      spotsRemaining: 34,
    },
  },
  {
    slug: "psychiatry",
    parentToken: "psychiatry",
    metaTitle: "Psychiatry Experts: $110-$320/hr, Remote",
    metaDescription:
      "Prescribing psychiatrists earn $110-$320/hr reviewing medical AI. Remote, a few hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Psychiatry Network",
    heroLabel: "psychiatric prescribing and risk judgment",
    heroTitle: "Your prescribing judgment, now worth $110-$320 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the serotonin syndrome, the 5150, the lithium titration. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a psychiatrist flagging an unsafe drug combination",
    detail: {
      summary:
        "Walk through a model's psychiatric notes, medication plans, and risk assessments the way you would supervise a PGY-3. Flag the contraindicated combination and explain why a model that quotes the DSM still misjudges suicidality.",
    },
    ctaTitle: "Ready to put your psychiatric judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need a prescriber's risk judgment, not memorized DSM criteria.",
    opportunity: {
      ...baseOpportunity,
      id: "psychiatry",
      token: "psychiatry",
      title: "Psychiatry",
      payRateMin: "110",
      payRateMax: "320",
      requirements: [
        "Board-certified psychiatrist, MD/DO with active DEA registration (US)",
        "Active or recent prescribing practice; subspecialty in child, addiction, or forensic psychiatry a plus",
      ],
      referralTitle: "Psychiatry",
      spotsRemaining: 47,
    },
  },
  {
    slug: "neurosurgery",
    parentToken: "neurosurgery",
    metaTitle: "Neurosurgery Experts: $150-$400/hr, Remote",
    metaDescription:
      "Board-certified neurosurgeons earn $150-$400/hr reviewing medical AI. Remote, a few hours a week, paid per task. No patients, no liability.",
    heroEyebrow: "Neurosurgery Network",
    heroLabel: "operative indication and surgical planning",
    heroTitle: "Your operative judgment, now worth $150-$400 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - clip versus coil, the approach around eloquent cortex, the case you decline. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a neurosurgeon rejecting an unsafe operative plan",
    detail: {
      summary:
        "Review a model's operative plans, indications, and post-op reasoning the way you would vet a chief resident at M&M. Flag the approach that crosses eloquent cortex and explain why a textbook-correct indication would still be malpractice here.",
    },
    ctaTitle: "Ready to put your operative judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to paid, remote projects from AI labs and healthtech teams that need real surgical decision-making, not memorized operative steps.",
    opportunity: {
      ...baseOpportunity,
      id: "neurosurgery",
      token: "neurosurgery",
      title: "Neurosurgery",
      payRateMin: "150",
      payRateMax: "400",
      requirements: [
        "Board-certified or board-eligible neurosurgeon (US, active or recent practice)",
        "Independent operative experience in cranial, spine, vascular, or functional neurosurgery",
      ],
      referralTitle: "Neurosurgery",
      spotsRemaining: 31,
    },
  },
  {
    slug: "cardiology",
    parentToken: "cardiology",
    metaTitle: "Cardiology Experts: $120-$330/hr, Remote",
    metaDescription:
      "Cardiologists earn $120-$330/hr to review AI-drafted ECG reads, GDMT plans, and risk scores. Remote, a few hours a week. No patients, no liability.",
    heroEyebrow: "Cardiology Network",
    heroLabel: "cardiac and cardiovascular work",
    heroTitle: "Your cardiology judgment, now worth $120-$330 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the troponin trends, Wellens patterns, and GDMT calls only a cardiologist makes. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a cardiologist catching a missed STEMI on a subtle ECG",
    detail: {
      summary:
        "Review AI-drafted ECG reads, heart-failure plans, and risk scores the way you'd check a fellow's consult: flag what's wrong, defend the standard of care. Fully remote, a few hours a week, paid per task.",
    },
    ctaTitle: "Put your cardiology judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and research teams that need real cardiac judgment, not memorized guidelines.",
    opportunity: {
      ...baseOpportunity,
      id: "cardiology",
      token: "cardiology",
      title: "Cardiology",
      payRateMin: "120",
      payRateMax: "330",
      requirements: [
        "Board-certified cardiologist (US, active or recent practice)",
        "Fellowship-trained in cardiovascular disease, EP, or heart failure",
      ],
      referralTitle: "Cardiology",
      spotsRemaining: 42,
    },
  },
  {
    slug: "interventional-cardiology",
    parentToken: "interventional-cardiology",
    metaTitle: "Interventional Cardiology Experts: $140-$380/hr, Remote",
    metaDescription:
      "Interventional cardiologists earn $140-$380/hr reviewing AI-drafted angiographic reads, PCI plans, and structural-heart calls. Remote, paid per task. No patients, no liability.",
    heroEyebrow: "Interventional Cardiology Network",
    heroLabel: "cath lab and structural heart work",
    heroTitle: "Your cath-lab judgment, now worth $140-$380 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the FFR calls, the lesion you stage instead of stenting, the TAVR candidate you send back. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an interventionalist flagging an FFR-negative lesion the model wanted to stent",
    detail: {
      summary:
        "Review AI-drafted angiographic reads, PCI plans, and structural-heart recommendations the way you'd check a case before wheeling in: flag the unneeded stent, defend the negative FFR. Remote, paid per task.",
    },
    ctaTitle: "Put your cath-lab judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and research teams that need real interventional judgment, not memorized appropriate-use criteria.",
    opportunity: {
      ...baseOpportunity,
      id: "interventional-cardiology",
      token: "interventional-cardiology",
      title: "Interventional Cardiology",
      payRateMin: "140",
      payRateMax: "380",
      requirements: [
        "Board-certified in interventional cardiology (US, active or recent practice)",
        "Active or recent cath-lab operator (PCI, FFR/iFR, or structural heart)",
      ],
      referralTitle: "Interventional Cardiology",
      spotsRemaining: 33,
    },
  },
  {
    slug: "pulmonology",
    parentToken: "pulmonology",
    metaTitle: "Pulmonology Experts: $110-$310/hr, Remote",
    metaDescription:
      "Pulmonologists earn $110-$310/hr reviewing AI-drafted PFT reads, COPD and ILD plans, and nodule calls. Remote, a few hours a week. No patients, no liability.",
    heroEyebrow: "Pulmonology Network",
    heroLabel: "pulmonary and respiratory medicine work",
    heroTitle: "Your pulmonology judgment, now worth $110-$310 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the GOLD group you assign, the nodule you follow, the restrictive pattern a model reads as normal. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a pulmonologist catching a restrictive pattern the model read as normal",
    detail: {
      summary:
        "Review AI-drafted PFT reads, COPD and ILD plans, and nodule recommendations the way you'd check a clinic consult: flag the missed restrictive defect, defend the right inhaler step. Remote, paid per task.",
    },
    ctaTitle: "Put your pulmonology judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and research teams that need real pulmonary judgment, not memorized guidelines.",
    opportunity: {
      ...baseOpportunity,
      id: "pulmonology",
      token: "pulmonology",
      title: "Pulmonology",
      payRateMin: "110",
      payRateMax: "310",
      requirements: [
        "Board-certified pulmonologist (US, active or recent practice)",
        "Fellowship-trained in pulmonary or pulmonary/critical care medicine",
      ],
      referralTitle: "Pulmonology",
      spotsRemaining: 47,
    },
  },
  {
    slug: "respiratory-medicine",
    parentToken: "respiratory-medicine",
    metaTitle: "Respiratory Therapist Experts: $80-$220/hr, Remote",
    metaDescription:
      "Registered respiratory therapists earn $80-$220/hr reviewing AI-drafted ventilator settings, ABG reads, and weaning plans. Remote, a few hours a week. No patients, no liability.",
    heroEyebrow: "Respiratory Medicine Network",
    heroLabel: "respiratory therapy and pulmonary care work",
    heroTitle: "Your respiratory-care judgment, now worth $80-$220 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the ABG you act on, the mode change you make before the alarm, the auto-PEEP off the waveform. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an RRT catching auto-PEEP the model missed on the ventilator waveform",
    detail: {
      summary:
        "Review AI-drafted ventilator settings, weaning plans, and ABG reads the way you'd check them at the bedside: flag the unsafe tidal volume, defend the right PEEP for ARDS. Remote, paid per task.",
    },
    ctaTitle: "Put your respiratory-care judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and research teams that need real respiratory therapy judgment, not memorized protocols.",
    opportunity: {
      ...baseOpportunity,
      id: "respiratory-medicine",
      token: "respiratory-medicine",
      title: "Respiratory Medicine",
      payRateMin: "80",
      payRateMax: "220",
      requirements: [
        "Registered Respiratory Therapist (RRT) credential, NBRC-certified (US)",
        "Active or recent bedside experience in mechanical ventilation and ABG management",
      ],
      referralTitle: "Respiratory Medicine",
      spotsRemaining: 61,
    },
  },
  {
    slug: "critical-care",
    parentToken: "critical-care",
    metaTitle: "Critical Care / ICU Experts: $130-$350/hr, Remote",
    metaDescription:
      "Intensivists earn $130-$350/hr reviewing AI-drafted sepsis assessments, vasopressor plans, and ICU triage calls. Remote, a few hours a week. No patients, no liability.",
    heroEyebrow: "Critical Care Network",
    heroLabel: "ICU and critical care work",
    heroTitle: "Your ICU judgment, now worth $130-$350 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the pressor you start before the pressure crashes, the source you chase in undifferentiated shock. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an intensivist catching early septic shock the model wrote off as stable",
    detail: {
      summary:
        "Review AI-drafted sepsis bundles, vasopressor and fluid plans, and ICU decisions the way you'd check a handoff: flag the under-resuscitated patient, defend norepinephrine before more crystalloid. Remote, paid per task.",
    },
    ctaTitle: "Put your ICU judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to AI labs and research teams that need real critical care judgment, not memorized bundles.",
    opportunity: {
      ...baseOpportunity,
      id: "critical-care",
      token: "critical-care",
      title: "Critical Care / ICU",
      payRateMin: "130",
      payRateMax: "350",
      requirements: [
        "Board-certified in critical care medicine (US, active or recent practice)",
        "Fellowship-trained intensivist (pulmonary/critical care, surgical, or anesthesia CC)",
      ],
      referralTitle: "Critical Care / ICU",
      spotsRemaining: 38,
    },
  },
  {
    slug: "radiology",
    parentToken: "radiology",
    metaTitle: "Radiology Experts: $130-$350/hr, Remote, Paid per task",
    metaDescription:
      "Diagnostic and interventional radiologists earn $130-$350/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Radiology Network",
    heroLabel: "diagnostic and interventional imaging work",
    heroTitle: "Your radiology read, now worth $130-$350 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week stress-testing medical AI - the subtle nodule, the LI-RADS call, the contrast protocol only a radiologist defends. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a radiologist's eye on a missed pulmonary nodule",
    detail: {
      summary:
        "Over-read a model's reports, BI-RADS and LI-RADS calls, and imaging recommendations the way you would a trainee's dictation. Catch the satisfaction-of-search miss a checklist never sees.",
    },
    ctaTitle: "Ready to put your read on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real image interpretation.",
    opportunity: {
      ...baseOpportunity,
      id: "radiology",
      token: "radiology",
      title: "Radiology (DR & IR)",
      payRateMin: "130",
      payRateMax: "350",
      requirements: [
        "Board-certified diagnostic or interventional radiologist (US, active or recent practice)",
        "Independent image interpretation and reporting experience (CT, MRI, US, mammography, or IR)",
      ],
      referralTitle: "Radiology (DR & IR)",
      spotsRemaining: 41,
    },
  },
  {
    slug: "pathology",
    parentToken: "pathology",
    metaTitle: "Pathology Experts: $120-$330/hr, Remote, Paid per task",
    metaDescription:
      "Anatomic and clinical pathologists earn $120-$330/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Pathology Network",
    heroLabel: "anatomic and clinical pathology work",
    heroTitle: "Your pathology sign-out, now worth $120-$330 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the Gleason pattern, the IHC panel, the margin call only a pathologist makes. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a pathologist's eye on an overcalled Gleason grade",
    detail: {
      summary:
        "Review a model's diagnoses, tumor grades, and synoptic reports the way you sign out a resident's case. Tell a mimic from real disease and name the CAP element it skipped.",
    },
    ctaTitle: "Ready to put your sign-out on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real diagnostic judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "pathology",
      token: "pathology",
      title: "Pathology",
      payRateMin: "120",
      payRateMax: "330",
      requirements: [
        "Board-certified pathologist in anatomic and/or clinical pathology (US, active or recent practice)",
        "Independent sign-out experience in surgical pathology, cytopathology, or laboratory medicine",
      ],
      referralTitle: "Pathology",
      spotsRemaining: 35,
    },
  },
  {
    slug: "obstetrics-gynecology",
    parentToken: "obstetrics-gynecology",
    metaTitle: "OB/GYN Experts: $120-$320/hr, Remote, Paid per task",
    metaDescription:
      "Board-certified OB/GYNs earn $120-$320/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "OB/GYN Network",
    heroLabel: "obstetric and gynecologic work",
    heroTitle: "Your OB/GYN judgment, now worth $120-$320 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the dating you trust over the LMP, the contraindication you catch, the surgical step you never skip. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an OB/GYN catching a missed preeclampsia workup",
    detail: {
      summary:
        "Read a model's prenatal notes, contraceptive counseling, and gyn surgical plans the way you review a resident on L&D. Catch the unsafe recommendation a guideline summary flattens.",
    },
    ctaTitle: "Ready to put your OB/GYN judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real obstetric and gynecologic judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "obstetrics-gynecology",
      token: "obstetrics-gynecology",
      title: "OB/GYN",
      payRateMin: "120",
      payRateMax: "320",
      requirements: [
        "Board-certified OB/GYN (US, active or recent practice)",
        "Residency-trained MD/DO with obstetric and gynecologic surgical experience",
      ],
      referralTitle: "OB/GYN",
      spotsRemaining: 44,
    },
  },
  {
    slug: "maternal-fetal-medicine",
    parentToken: "maternal-fetal-medicine",
    metaTitle: "Maternal-Fetal Medicine: $140-$380/hr, Remote, Paid per task",
    metaDescription:
      "MFM subspecialists earn $140-$380/hr to pressure-test medical AI on high-risk pregnancy, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Maternal-Fetal Medicine Network",
    heroLabel: "high-risk pregnancy work",
    heroTitle: "Your perinatology judgment, now worth $140-$380 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the growth restriction, the fetal echo finding, the delivery timing only an MFM specialist calls. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an MFM specialist catching a misread fetal echo",
    detail: {
      summary:
        "Review a model's high-risk plans, fetal anomaly reads, and preeclampsia management the way you staff a complex consult. Weigh two patients at once where the data conflict.",
    },
    ctaTitle:
      "Ready to put your maternal-fetal medicine judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real high-risk obstetric judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "maternal-fetal-medicine",
      token: "maternal-fetal-medicine",
      title: "Maternal-Fetal Medicine",
      payRateMin: "140",
      payRateMax: "380",
      requirements: [
        "Fellowship-trained, board-certified or board-eligible in Maternal-Fetal Medicine (US)",
        "Active or recent perinatology practice managing high-risk pregnancies",
      ],
      referralTitle: "Maternal-Fetal Medicine",
      spotsRemaining: 31,
    },
  },
  {
    slug: "emergency-medicine",
    parentToken: "emergency-medicine",
    metaTitle: "Emergency Medicine: $120-$340/hr, Remote, Paid per task",
    metaDescription:
      "Emergency physicians earn $120-$340/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Emergency Medicine Network",
    heroLabel: "emergency and resuscitation work",
    heroTitle: "Your ED judgment, now worth $120-$340 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the ESI level, the can't-miss diagnosis, the resuscitation only an ED attending runs. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an attending catching the STEMI hiding behind a normal first ECG",
    detail: {
      summary:
        "Review a model's triage calls, dispositions, and resuscitation plans the way you supervise a senior resident running the board. Catch the time-critical diagnosis a textbook algorithm misses.",
    },
    ctaTitle: "Ready to put your ED judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real emergency department judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "emergency-medicine",
      token: "emergency-medicine",
      title: "Emergency Medicine",
      payRateMin: "120",
      payRateMax: "340",
      requirements: [
        "Board-certified or board-eligible in Emergency Medicine (US, active or recent practice)",
        "ED attending, EM resident graduate, or fellowship-trained acute care physician",
      ],
      referralTitle: "Emergency Medicine",
      spotsRemaining: 42,
    },
  },
  {
    slug: "acute-care",
    parentToken: "acute-care",
    metaTitle: "Acute Care Experts: $100-$280/hr, Remote, Paid per task",
    metaDescription:
      "Hospitalists and acute care NPs and PAs earn $100-$280/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Acute Care Network",
    heroLabel: "inpatient stabilization work",
    heroTitle: "Your acute care judgment, now worth $100-$280 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the early warning score, the rapid response, the bridge to the ICU only an inpatient clinician builds. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a hospitalist catching sepsis two hours before the lactate spikes",
    detail: {
      summary:
        "Review a model's stabilization plans, escalation calls, and inpatient notes the way you check a covering clinician's overnight. Catch the quiet decompensation before the vitals confirm it.",
    },
    ctaTitle: "Ready to put your acute care judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real inpatient stabilization judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "acute-care",
      token: "acute-care",
      title: "Acute Care",
      payRateMin: "100",
      payRateMax: "280",
      requirements: [
        "Hospital-based acute care clinician: hospitalist MD/DO, acute care NP (ACNP/AGACNP), or acute care PA (US)",
        "Active or recent inpatient stabilization, step-down, or hospital medicine experience",
      ],
      referralTitle: "Acute Care",
      spotsRemaining: 67,
    },
  },
  {
    slug: "urgent-care",
    parentToken: "urgent-care",
    metaTitle: "Urgent Care Experts: $90-$260/hr, Remote, Paid per task",
    metaDescription:
      "Urgent care physicians, NPs, and PAs earn $90-$260/hr to pressure-test medical AI, remotely and a few hours a week. Paid per task. No patients, no liability.",
    heroEyebrow: "Urgent Care Network",
    heroLabel: "ambulatory urgent care work",
    heroTitle: "Your urgent care judgment, now worth $90-$260 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week pressure-testing medical AI - the laceration you close or refer, the chest pain you send to the ED, the one sick kid you catch. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an urgent care clinician routing a subtle appendicitis to the ED before it perforates",
    detail: {
      summary:
        "Review a model's urgent care plans, discharge instructions, and referral calls the way you check a new provider on the fast track. Catch the red flag it treated as routine.",
    },
    ctaTitle: "Ready to put your urgent care judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real ambulatory triage judgment.",
    opportunity: {
      ...baseOpportunity,
      id: "urgent-care",
      token: "urgent-care",
      title: "Urgent Care",
      payRateMin: "90",
      payRateMax: "260",
      requirements: [
        "Urgent care or ambulatory care clinician: physician (FM/EM/IM), NP, or PA (US)",
        "Active or recent fast-track, walk-in, or urgent care practice experience",
      ],
      referralTitle: "Urgent Care",
      spotsRemaining: 54,
    },
  },
  {
    slug: "general-surgery",
    parentToken: "general-surgery",
    metaTitle: "General Surgeons: $140-$380/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $140-$380/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "General Surgery Network",
    heroLabel: "operative and acute care work",
    heroTitle: "Your operative judgment, now worth $140-$380 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the rigid abdomen that belongs in the OR, the anastomosis you would not staple. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a surgeon's read on a missed mesenteric ischemia",
    detail: {
      summary:
        "You grade a model's operative plans, triage notes, and post-op management, catching the perforated viscus sent to CT instead of the OR. You explain the conversion threshold no algorithm weighs, remote and paid by the hour.",
    },
    ctaTitle: "Put your operative judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real surgeon's read.",
    opportunity: {
      ...baseOpportunity,
      id: "general-surgery",
      token: "general-surgery",
      title: "General Surgery",
      payRateMin: "140",
      payRateMax: "380",
      requirements: [
        "Board-certified general surgeon (US, active or recent operative practice)",
        "Fellowship or acute care surgery experience with MIS and emergency cases",
      ],
      referralTitle: "General Surgery",
      spotsRemaining: 42,
    },
  },
  {
    slug: "orthopedic-surgery",
    parentToken: "orthopedic-surgery",
    metaTitle: "Orthopedic Surgeons: $150-$400/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $150-$400/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Orthopedic Surgery Network",
    heroLabel: "musculoskeletal and operative work",
    heroTitle: "Your operative judgment, now worth $150-$400 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the Gustilo grade, the implant for a displaced femoral neck, the early revision call. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an orthopod's eye on a missed compartment syndrome",
    detail: {
      summary:
        "You grade a model's fracture classifications, operative templates, and rehab protocols, catching the impending compartment syndrome it misses. You explain the bone quality and patient factors that change the construct, remote and paid by the hour.",
    },
    ctaTitle: "Put your operative judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real surgeon's read.",
    opportunity: {
      ...baseOpportunity,
      id: "orthopedic-surgery",
      token: "orthopedic-surgery",
      title: "Orthopedic Surgery",
      payRateMin: "150",
      payRateMax: "400",
      requirements: [
        "Board-certified orthopedic surgeon (US, active or recent operative practice)",
        "Fellowship training in trauma, arthroplasty, sports, spine, or hand",
      ],
      referralTitle: "Orthopedic Surgery",
      spotsRemaining: 36,
    },
  },
  {
    slug: "urology",
    parentToken: "urology",
    metaTitle: "Urologists: $130-$360/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $130-$360/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Urology Network",
    heroLabel: "genitourinary and surgical work",
    heroTitle: "Your urologic judgment, now worth $130-$360 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the infected stone you decompress tonight, the surveillance call on a low-risk prostate cancer. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a urologist's read on a missed obstructing infected stone",
    detail: {
      summary:
        "You grade a model's workups, surgical plans, and oncology recommendations, catching the infected obstructed kidney it watches instead of decompresses. You explain the AUA nuance a guideline summary flattens, remote and paid by the hour.",
    },
    ctaTitle: "Put your urologic judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real urologist's read.",
    opportunity: {
      ...baseOpportunity,
      id: "urology",
      token: "urology",
      title: "Urology",
      payRateMin: "130",
      payRateMax: "360",
      requirements: [
        "Board-certified urologist (US, active or recent practice)",
        "Endourology, oncology, or reconstructive subspecialty experience",
      ],
      referralTitle: "Urology",
      spotsRemaining: 48,
    },
  },
  {
    slug: "ophthalmology",
    parentToken: "ophthalmology",
    metaTitle: "Ophthalmologists: $130-$350/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $130-$350/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Ophthalmology Network",
    heroLabel: "vision and microsurgical work",
    heroTitle: "Your clinical eye, now worth $130-$350 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the disc you call progression, the macula-on detachment you operate today. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an ophthalmologist's eye on a missed macula-on detachment",
    detail: {
      summary:
        "You grade a model's diagnoses, OCT and fundus reads, and surgical plans, catching the macula-on detachment it calls routine. You explain the AAO-aligned timing a checklist leaves out, remote and paid by the hour.",
    },
    ctaTitle: "Put your clinical eye to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real ophthalmologist's read.",
    opportunity: {
      ...baseOpportunity,
      id: "ophthalmology",
      token: "ophthalmology",
      title: "Ophthalmology",
      payRateMin: "130",
      payRateMax: "350",
      requirements: [
        "Board-certified ophthalmologist (US, active or recent practice)",
        "Retina, glaucoma, cornea, or cataract subspecialty experience",
      ],
      referralTitle: "Ophthalmology",
      spotsRemaining: 51,
    },
  },
  {
    slug: "anesthesiology",
    parentToken: "anesthesiology",
    metaTitle: "Anesthesiologists: $130-$360/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $130-$360/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Anesthesiology Network",
    heroLabel: "perioperative and airway work",
    heroTitle: "Your perioperative judgment, now worth $130-$360 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the difficult airway you plan ahead, the case you delay for an unoptimized comorbidity. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an anesthesiologist's read on a missed difficult airway",
    detail: {
      summary:
        "You grade a model's preop assessments, airway plans, and intraoperative management, catching the predicted difficult airway it misses. You explain why you would delay a case for unoptimized cardiac risk, remote and paid by the hour.",
    },
    ctaTitle: "Put your perioperative judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real anesthesiologist's read.",
    opportunity: {
      ...baseOpportunity,
      id: "anesthesiology",
      token: "anesthesiology",
      title: "Anesthesiology",
      payRateMin: "130",
      payRateMax: "360",
      requirements: [
        "Board-certified anesthesiologist (US, active or recent practice)",
        "Cardiac, regional, pediatric, or critical care subspecialty experience",
      ],
      referralTitle: "Anesthesiology",
      spotsRemaining: 44,
    },
  },
  {
    slug: "dermatology",
    parentToken: "dermatology",
    metaTitle: "Dermatologists: $120-$340/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $120-$340/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Dermatology Network",
    heroLabel: "cutaneous diagnostic work",
    heroTitle: "Your diagnostic eye, now worth $120-$340 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the pattern you flag for excision, the melanoma you stage by Breslow depth. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a dermatologist's eye on a missed amelanotic melanoma",
    detail: {
      summary:
        "You grade a model's lesion reads, differentials, and management plans, catching the amelanotic melanoma it calls benign. You explain the morphology and distribution a textbook caption leaves out, remote and paid by the hour.",
    },
    ctaTitle: "Put your diagnostic eye to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real dermatologist's read.",
    opportunity: {
      ...baseOpportunity,
      id: "dermatology",
      token: "dermatology",
      title: "Dermatology",
      payRateMin: "120",
      payRateMax: "340",
      requirements: [
        "Board-certified dermatologist (US, active or recent practice)",
        "Dermoscopy, Mohs, or dermatopathology subspecialty experience",
      ],
      referralTitle: "Dermatology",
      spotsRemaining: 57,
    },
  },
  {
    slug: "otolaryngology",
    parentToken: "otolaryngology",
    metaTitle: "ENT Surgeons: $130-$360/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $130-$360/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "ENT (Otolaryngology) Network",
    heroLabel: "head, neck, and airway work",
    heroTitle: "Your surgical judgment, now worth $130-$360 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the stridor you take to the OR, the neck mass you work up. Paid per task. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "an ENT surgeon's read on a missed supraglottitis",
    detail: {
      summary:
        "You grade a model's workups, operative plans, and airway management, catching the closing pediatric airway it observes instead of secures. You explain the head and neck nuance a guideline flattens, remote and paid by the hour.",
    },
    ctaTitle: "Put your surgical judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real ENT surgeon's read.",
    opportunity: {
      ...baseOpportunity,
      id: "otolaryngology",
      token: "otolaryngology",
      title: "ENT (Otolaryngology)",
      payRateMin: "130",
      payRateMax: "360",
      requirements: [
        "Board-certified otolaryngologist (US, active or recent practice)",
        "Otology, head and neck, laryngology, or rhinology subspecialty experience",
      ],
      referralTitle: "ENT (Otolaryngology)",
      spotsRemaining: 39,
    },
  },
  {
    slug: "plastic-surgery",
    parentToken: "plastic-surgery",
    metaTitle: "Plastic Surgeons: $140-$390/hr Reviewing Medical AI, Remote",
    metaDescription:
      "Get paid $140-$390/hr to pressure-test medical AI from home. A few hours a week, paid per task, on your schedule. No patients, no liability.",
    heroEyebrow: "Plastic Surgery Network",
    heroLabel: "reconstructive and aesthetic work",
    heroTitle: "Your reconstructive judgment, now worth $140-$390 an hour.",
    heroSubtitle:
      "A few remote hours a week reviewing medical AI - the flap for a complex wound, the transfer you would not risk on a smoker. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a plastic surgeon's eye on a flap doomed by poor perfusion",
    detail: {
      summary:
        "You grade a model's reconstructive plans, flap selections, and aesthetic recommendations, catching the free flap it picks with no recipient vessels. You explain the perfusion and donor site factors a checklist leaves out, remote and paid by the hour.",
    },
    ctaTitle: "Put your reconstructive judgment to work.",
    ctaSubtitle:
      "Apply once. Get matched to remote, paid work from AI labs and healthtech teams that need a real plastic surgeon's read.",
    opportunity: {
      ...baseOpportunity,
      id: "plastic-surgery",
      token: "plastic-surgery",
      title: "Plastic Surgery",
      payRateMin: "140",
      payRateMax: "390",
      requirements: [
        "Board-certified plastic surgeon (US, active or recent operative practice)",
        "Microsurgery, reconstructive, craniofacial, or aesthetic subspecialty experience",
      ],
      referralTitle: "Plastic Surgery",
      spotsRemaining: 33,
    },
  },
  {
    slug: "endocrinology",
    parentToken: "endocrinology",
    metaTitle: "Endocrinology Experts: Get Paid $110-$310/hr Remote",
    metaDescription:
      "Board-certified endocrinologists earn $110-$310/hr, remote and hourly, reviewing medical AI on diabetes, thyroid, pituitary, and adrenal cases. A few hours a week. No patients, no liability.",
    heroEyebrow: "Endocrinology Network",
    heroLabel: "endocrine and metabolic work",
    heroTitle: "Your endocrine judgment, now worth $110-$310 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the insulin titrations, TSH-against-free-T4 reads, and adrenal workups only an endocrinologist makes. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an endocrinologist catching a missed secondary cause of hypertension",
    detail: {
      summary:
        "Review AI-drafted insulin titrations, thyroid workups, and adrenal plans the way you'd review a fellow's. Flag the regimen that ignores hypoglycemia risk and explain why a normal TSH doesn't close the case.",
    },
    ctaTitle: "Ready to put your endocrine judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real metabolic reasoning, not memorized cutoffs.",
    opportunity: {
      ...baseOpportunity,
      id: "endocrinology",
      token: "endocrinology",
      title: "Endocrinology",
      payRateMin: "110",
      payRateMax: "310",
      requirements: [
        "Board-certified endocrinologist (US, active or recent practice)",
        "Fellowship-trained in endocrinology, diabetes, & metabolism",
      ],
      referralTitle: "Endocrinology",
      spotsRemaining: 41,
    },
  },
  {
    slug: "gastroenterology",
    parentToken: "gastroenterology",
    metaTitle: "Gastroenterology Experts: Get Paid $130-$360/hr Remote",
    metaDescription:
      "Board-certified gastroenterologists and hepatologists earn $130-$360/hr, remote and hourly, reviewing medical AI on endoscopy, IBD, and liver cases. A few hours a week. No patients, no liability.",
    heroEyebrow: "Gastroenterology Network",
    heroLabel: "GI and hepatology work",
    heroTitle: "Your GI judgment, now worth $130-$360 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the surveillance intervals, IBD step-ups, and LFT pattern reads only a gastroenterologist makes. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a gastroenterologist catching a too-long colonoscopy surveillance interval",
    detail: {
      summary:
        "Review AI-drafted endoscopy plans, IBD escalations, and cirrhosis management the way you'd review a colleague's note. Flag the surveillance interval that violates ACG guidance and explain why those LFTs read cholestatic, not hepatocellular.",
    },
    ctaTitle: "Ready to put your GI judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real endoscopic and hepatology reasoning, not memorized algorithms.",
    opportunity: {
      ...baseOpportunity,
      id: "gastroenterology",
      token: "gastroenterology",
      title: "Gastroenterology",
      payRateMin: "130",
      payRateMax: "360",
      requirements: [
        "Board-certified gastroenterologist or hepatologist (US, active or recent practice)",
        "Fellowship-trained in gastroenterology with endoscopy experience",
      ],
      referralTitle: "Gastroenterology",
      spotsRemaining: 36,
    },
  },
  {
    slug: "nephrology",
    parentToken: "nephrology",
    metaTitle: "Nephrology Experts: Get Paid $110-$310/hr Remote",
    metaDescription:
      "Board-certified nephrologists earn $110-$310/hr, remote and hourly, reviewing medical AI on CKD staging, dialysis, and acid-base and electrolyte cases. A few hours a week. No patients, no liability.",
    heroEyebrow: "Nephrology Network",
    heroLabel: "kidney and dialysis work",
    heroTitle: "Your nephrology judgment, now worth $110-$310 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the sodium correction rates, acid-base reads, and dialysis prescriptions only a nephrologist makes. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample: "a nephrologist catching an overly fast sodium correction",
    detail: {
      summary:
        "Review AI-drafted electrolyte corrections, CKD plans, and dialysis decisions the way you'd review a fellow's. Flag the sodium correction that risks osmotic demyelination and explain why that anion gap doesn't account for the bicarbonate.",
    },
    ctaTitle: "Ready to put your nephrology judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real renal and acid-base reasoning, not memorized formulas.",
    opportunity: {
      ...baseOpportunity,
      id: "nephrology",
      token: "nephrology",
      title: "Nephrology",
      payRateMin: "110",
      payRateMax: "310",
      requirements: [
        "Board-certified nephrologist (US, active or recent practice)",
        "Fellowship-trained in nephrology with dialysis management experience",
      ],
      referralTitle: "Nephrology",
      spotsRemaining: 47,
    },
  },
  {
    slug: "rheumatology",
    parentToken: "rheumatology",
    metaTitle: "Rheumatology Experts: Get Paid $110-$310/hr Remote",
    metaDescription:
      "Board-certified rheumatologists earn $110-$310/hr, remote and hourly, reviewing medical AI on autoimmune workups, biologic selection, and disease activity scoring. A few hours a week. No patients, no liability.",
    heroEyebrow: "Rheumatology Network",
    heroLabel: "autoimmune and biologics work",
    heroTitle: "Your rheumatology judgment, now worth $110-$310 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the ANA-against-the-picture reads, DMARD sequencing, and pre-biologic TB screens only a rheumatologist makes. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "a rheumatologist catching a biologic started without latent TB screening",
    detail: {
      summary:
        "Review AI-drafted autoimmune differentials, biologic selections, and disease activity scores the way you'd review a fellow's consult. Flag the TNF inhibitor started without TB screening and explain why a positive ANA alone doesn't make the diagnosis.",
    },
    ctaTitle: "Ready to put your rheumatology judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real autoimmune and biologic reasoning, not memorized antibody panels.",
    opportunity: {
      ...baseOpportunity,
      id: "rheumatology",
      token: "rheumatology",
      title: "Rheumatology",
      payRateMin: "110",
      payRateMax: "310",
      requirements: [
        "Board-certified rheumatologist (US, active or recent practice)",
        "Fellowship-trained in rheumatology with biologic management experience",
      ],
      referralTitle: "Rheumatology",
      spotsRemaining: 52,
    },
  },
  {
    slug: "infectious-disease",
    parentToken: "infectious-disease",
    metaTitle: "Infectious Disease Experts: Get Paid $110-$320/hr Remote",
    metaDescription:
      "Board-certified infectious disease physicians earn $110-$320/hr, remote and hourly, reviewing medical AI on antimicrobial regimens, stewardship calls, and OPAT plans. A few hours a week. No patients, no liability.",
    heroEyebrow: "Infectious Disease Network",
    heroLabel: "antimicrobial and stewardship work",
    heroTitle: "Your infectious disease judgment, now worth $110-$320 an hour.",
    heroSubtitle:
      "Spend a few remote hours a week reviewing medical AI - the antibiogram reads, de-escalation calls, and OPAT durations only an ID physician makes. Paid hourly. No patients, no liability.",
    badgePersonaId: "doctor",
    letterExample:
      "an ID physician catching a missed chance to de-escalate broad-spectrum coverage",
    detail: {
      summary:
        "Review AI-drafted antimicrobial regimens, stewardship calls, and OPAT plans the way you'd review a fellow's consult. Flag the broad-spectrum coverage that should have been de-escalated and explain why local resistance changes the empiric choice.",
    },
    ctaTitle: "Ready to put your infectious disease judgment on the record?",
    ctaSubtitle:
      "Apply once. Get matched to remote, hourly projects from AI labs and healthtech teams that need real antimicrobial and stewardship reasoning, not memorized drug charts.",
    opportunity: {
      ...baseOpportunity,
      id: "infectious-disease",
      token: "infectious-disease",
      title: "Infectious Disease",
      payRateMin: "110",
      payRateMax: "320",
      requirements: [
        "Board-certified infectious disease physician (US, active or recent practice)",
        "Fellowship-trained in infectious disease with stewardship experience",
      ],
      referralTitle: "Infectious Disease",
      spotsRemaining: 44,
    },
  },
];

export function getAudience(slug: string): AudienceConfig | undefined {
  return AUDIENCES.find((a) => a.slug === slug);
}

export function getAudienceByToken(token: string): AudienceConfig | undefined {
  return AUDIENCES.find((a) => a.parentToken === token);
}

// Clusters of related audiences for parent <-> child internal linking. Sub
// pages and their parent vertical sit in the same group so each page links to
// its siblings, which spreads link equity and gets the long-tail sub pages
// indexed instead of orphaned. Every slug should appear in exactly one cluster;
// the invariant test enforces that.
const AUDIENCE_CLUSTERS: AudienceSlug[][] = [
  [
    "finance",
    "fpa-analysts",
    "treasury-analysts",
    "credit-risk-analysts",
    "corporate-development",
    "quant-finance",
    "actuaries",
  ],
  [
    "accounting",
    "tax-accountants",
    "auditors",
    "controllers",
    "forensic-accountants",
  ],
  [
    "healthcare-ops",
    "medical-coders",
    "clinical-documentation",
    "revenue-cycle",
    "utilization-management",
  ],
  ["physicians", "nurses", "pharmacists", "mental-health"],
  [
    "software-engineering",
    "backend-engineers",
    "frontend-engineers",
    "ml-engineers",
    "devops-sre",
    "data-engineers",
    "data-scientists",
    "cybersecurity",
  ],
  [
    "management-consultants",
    "strategy-consultants",
    "operations-consultants",
    "ex-mbb-consultants",
  ],
  [
    "product-experts",
    "product-managers",
    "technical-program-managers",
    "growth-product-managers",
    "ai-product-managers",
    "ux-designers",
  ],
  ["mechanical-engineers", "electrical-engineers", "civil-engineers"],
  ["biology-life-sciences", "chemistry"],
  ["lawyers", "economists", "academics"],
  ["marketing-experts", "sales-experts", "supply-chain"],
  ["writers-editors", "translators"],
  // Medical specialties
  [
    "primary-care",
    "internal-medicine",
    "family-medicine",
    "hospital-medicine",
    "pediatrics",
  ],
  [
    "medical-oncology",
    "oncology-subspecialties",
    "radiation-oncology",
    "surgical-oncology",
  ],
  ["neurology", "psychiatry", "neurosurgery"],
  [
    "cardiology",
    "interventional-cardiology",
    "pulmonology",
    "respiratory-medicine",
    "critical-care",
  ],
  ["radiology", "pathology"],
  ["obstetrics-gynecology", "maternal-fetal-medicine"],
  ["emergency-medicine", "acute-care", "urgent-care"],
  [
    "general-surgery",
    "orthopedic-surgery",
    "urology",
    "ophthalmology",
    "anesthesiology",
    "dermatology",
    "otolaryngology",
    "plastic-surgery",
  ],
  [
    "endocrinology",
    "gastroenterology",
    "nephrology",
    "rheumatology",
    "infectious-disease",
  ],
];

// Up to `limit` sibling audiences from the same cluster, in cluster order.
export function getRelatedAudiences(
  slug: AudienceSlug,
  limit = 5,
): AudienceConfig[] {
  const cluster = AUDIENCE_CLUSTERS.find((group) => group.includes(slug));
  if (!cluster) {
    return [];
  }
  return cluster
    .filter((s) => s !== slug)
    .map((s) => getAudience(s))
    .filter((a): a is AudienceConfig => Boolean(a))
    .slice(0, limit);
}
